/**
 * Help Content Configuration
 * Centralized location for all help content in the application
 * 
 * Structure:
 * - Key: Feature identifier (e.g., 'weigh-bridge-receipts')
 * - Value: Object with title and content (React elements)
 */

import React from 'react';

export const helpContent = {
  'quality-grades': {
    title: 'Organization Quality Grades',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Define grade scales for categories used by your materials and products
          (e.g. Sugarcane Grade A / Grade B). QC inspectors use these grades when inspecting lots.
        </p>
        <h5 className="mt-4">Item-specific grades</h5>
        <p>
          If one category groups dissimilar materials (e.g. "Inorganic Chemicals" containing both
          Sodium Hydrosulfite and unrelated compounds), use the <strong>Applies to</strong> picker
          to scope a grade to just one item instead of the whole type. QC resolution checks the
          item's own grades first, then the category's, then System Admin templates.
        </p>
        <h5 className="mt-4">Auto-grade rules</h5>
        <p>
          Add measurement criteria such as Brix min 18 for Grade A, or Brix 14–17 for Grade B.
          When the inspector enters Moisture=12% and Brix=19, the ERP selects Grade A automatically.
        </p>
        <h5 className="mt-4">Reference rate</h5>
        <p className="mb-0">
          Optional premium/base rate per grade with a unit of measure
          (e.g. ₹3,500 / ton). Stored for later farmer payment and routing;
          payment posting is not automatic yet.
        </p>
      </div>
    )
  },
  'quality': {
    title: 'Quality Check',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          After a GRN is set to RECEIVED (or PARTIAL), material lots start as <strong>Pending QC</strong>.
          They are not available to issue on a GIN until an inspection passes all or part of the quantity.
        </p>
        <h5 className="mt-4">Measurements &amp; grades</h5>
        <p>
          Enter lab values (Brix, Moisture, …) defined under Org Admin → Quality Grades.
          The ERP auto-calculates the matching grade; you can override it manually.
        </p>
        <h5 className="mt-4">Results</h5>
        <ul>
          <li><strong>Pass</strong> — clears remaining pending quantity; lot becomes issuable</li>
          <li><strong>Partial</strong> — only the passed quantity becomes issuable; rest stays pending</li>
          <li><strong>Fail</strong> — hold or scrap; quantity stays non-issuable</li>
        </ul>
        <p className="mb-0">
          Stock transfer receipts skip QC (already cleared at source).
        </p>
      </div>
    )
  },
  'material-master': {
    title: 'Material Master',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          System-wide catalog of materials (items) and categories. Organizations link these items
          under Org Admin → Materials for purchasing and inventory.
        </p>
        <h5 className="mt-4">Tabs</h5>
        <ul>
          <li><strong>Items</strong> — catalog materials with values for their type&apos;s attributes</li>
          <li><strong>Item Requests</strong> — materials organizations made because they could not find theirs, waiting for a decision, and a searchable history of what was decided (approved and kept private, made available to everyone, merged into a shared item, removed, or filed under a parent item such as Padlock while staying its own material). A merge or removal leaves the draft gone, but the history keeps its name, category and HSN as they were. The organization&apos;s Org Admin sees the outcome under Org Admin → Materials until they dismiss it</li>
          <li><strong>Categories</strong> — classification plus the attribute definitions for that type. Search by name, parent, description or organization, and filter by shared or private to an organization, and active or inactive; a match is shown under its parents</li>
        </ul>
        <h5 className="mt-4">Attributes on Category</h5>
        <ol>
          <li>Open <strong>Categories</strong> and click <strong>Attributes</strong> for a type</li>
          <li>Define fields (code, name, data type, optional unit, Select options)</li>
          <li>When creating an <strong>Item</strong> of that type, fill those attribute values</li>
        </ol>
        <p>
          Each category owns its own attribute list. Two types may both have &quot;Grade&quot; with
          different allowed options — definitions are not shared globally.
        </p>
        <p>
          Catalog attributes describe the material. Org-specific inventory parameters (MOQ, ROP, org SKU)
          remain on Org Admin → Materials.
        </p>
        <h5 className="mt-4">What organizations add</h5>
        <p>
          An Org Admin who cannot find a material can make their own item, and their own categories with their own
          attributes. These show here marked <strong>Private to</strong> the organization; no other organization sees them.
        </p>
        <ul>
          <li><strong>Edit</strong> tidies a name, description or unit without making it shared.</li>
          <li><strong>Approve (keep private)</strong> fills in the GTIN and HS code, lets you move the item to the right category (a shared one, or one of that organization&apos;s own), and leaves the item with the organization that made it (the row says whether they use it as a raw material, a product or both); <strong>Make available to everyone</strong> shares it instead; <strong>Merge into…</strong> joins a duplicate into the right item and moves everything that used it.</li>
          <li><strong>Make available to everyone</strong> takes the owner off an item (and its category and attributes, if those were the organization&apos;s own). It is refused if a shared one already has that name. The organization keeps using it as before but can no longer edit it.</li>
          <li>Once any item has a value for an attribute, its code and kind cannot change and an option in use cannot be removed; a used attribute is switched off instead of deleted.</li>
        </ul>
      </div>
    )
  },
  'procurement-surveys': {
    title: 'Procurement Surveys',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Procurement Surveys (Field Slips) capture a field officer&apos;s assessment of material
          available for procurement. The model is industry-agnostic: common fields are stored
          directly; crop- or material-specific attributes can be added later via an extensible attributes map.
        </p>
        <h5 className="mt-4">Workflow</h5>
        <ol>
          <li><strong>New</strong> — created by a field officer; editable; usable for WBR and GRN</li>
          <li><strong>In Progress</strong> — set automatically when the first GRN is linked</li>
          <li><strong>Completed</strong> — field weighing finished (comments optional). Still selectable on GRN so warehouse can receipt; new WBRs are not allowed</li>
          <li><strong>Cancelled</strong> — voided (e.g. farmer not selling); comments required; not usable for WBR or GRN. Cannot cancel after a GRN has been created against the survey</li>
        </ol>
        <h5 className="mt-4">Common Fields</h5>
        <ul>
          <li>Survey Date, Surveyor / Field Officer</li>
          <li>Material (from Org Admin → Materials)</li>
          <li>Supply Vendor (farmer) and H&amp;T Vendor</li>
          <li>Vendor Site address (site name, address, city, district, state, country, postal code)</li>
          <li>Area (acres), Estimated Quantity with UOM (default Kg), Binding Deductions (%), Price</li>
          <li>Price currency comes from Org settings; if that conflicts with organization/site country, you are asked to confirm currency</li>
        </ul>
        <h5 className="mt-4">GRN Integration</h5>
        <p>
          In Inventory → Goods Receipt Notes, choose movement type
          <strong> Receive against Procurement Survey</strong> (or any inbound type with
          Reference Type = Procurement Survey), then select a New, In Progress, or Completed survey
          instead of a Purchase Order when receiving material.
        </p>
        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Use <strong>View &amp; Print</strong> to open a printable field slip for any survey</li>
          <li>Use <strong>View Transactions</strong> to see linked Weigh Bridge Receipts and GRNs with received line items</li>
          <li>Complete the survey when field weighing is finished; it remains usable for GRN</li>
          <li>Cancel is only allowed before a GRN is created against the survey</li>
        </ul>
      </div>
    )
  },
  // Purchase Module - Weigh Bridge Receipts
  'weigh-bridge-receipts': {
    title: 'Weigh Bridge Receipts',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Weigh Bridge Receipts (WBR) are used to record vehicle weights at weigh bridge stations. 
          This feature helps track the gross weight, empty weight, and calculate net weight of materials 
          being transported.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>RST Number:</strong> Receipt Serial Ticket number - auto-generated if left empty</li>
          <li><strong>Vendor:</strong> The supplier providing the material</li>
          <li><strong>Transporter:</strong> The vendor responsible for transportation (must be of type H&T or Transporter)</li>
          <li><strong>Reference Type:</strong> Optional link to a Purchase Order or a Procurement Survey / Field Slip (not both)</li>
          <li><strong>Material:</strong> The item being weighed</li>
          <li><strong>Gross Weight:</strong> Total weight of vehicle with material</li>
          <li><strong>Empty Weight:</strong> Weight of empty vehicle</li>
          <li><strong>Net Weight:</strong> Automatically calculated as Gross Weight - Empty Weight</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create Receipt"</strong> to create a new weigh bridge receipt</li>
          <li>Fill in the required fields (Date, Vendor, Material, Gross Weight, Empty Weight)</li>
          <li>Optionally choose a Reference Type:
            <ul>
              <li><strong>Purchase Order</strong> — link an incomplete PO (filtered by vendor/material when set)</li>
              <li><strong>Procurement Survey</strong> — link a New or In Progress field slip; vendor, material, transporter, and address autofill from the survey</li>
            </ul>
          </li>
          <li>The system will automatically calculate the net weight</li>
          <li>Save the receipt - it will be available for use in GRN (Goods Receipt Note) creation</li>
        </ol>

        <h5 className="mt-4">Integration with GRN</h5>
        <p>
          When creating a Goods Receipt Note (GRN), you can select a weigh bridge receipt to automatically 
          populate the received quantity with the net weight from the receipt. This ensures accurate tracking 
          of material quantities received.
        </p>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Always verify gross and empty weights before saving</li>
          <li>Link to a Purchase Order or Procurement Survey when possible for better traceability</li>
          <li>Use the "View & Print" option to generate a printable receipt</li>
          <li>Weigh bridge receipts can be edited or deleted if not yet used in a GRN</li>
        </ul>
      </div>
    )
  },

  // Purchase Module - Stock Levels
  'stock-levels': {
    title: 'Stock Levels',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Stock Levels provides a real-time view of inventory quantities across all warehouses. 
          This feature helps you monitor current stock availability, identify low stock items, and 
          manage inventory across multiple warehouse locations.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Warehouse Filter:</strong> View stock levels for a specific warehouse or all warehouses</li>
          <li><strong>Item Details:</strong> See item name, warehouse location, and current quantity</li>
          <li><strong>Real-time Updates:</strong> Stock levels update automatically when GRNs or GINs are processed</li>
          <li><strong>Refresh:</strong> Manually refresh to get the latest stock information</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Select a warehouse from the dropdown to filter stock levels, or choose "All Warehouses"</li>
          <li>Review the stock levels table to see current quantities</li>
          <li>Use the <strong>"Refresh"</strong> button to update stock levels manually</li>
          <li>Items with zero stock are not displayed by default</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Stock levels are automatically updated when you create GRNs (receiving goods) or GINs (issuing goods)</li>
          <li>Use warehouse filters to focus on specific locations</li>
          <li>Monitor stock levels regularly to avoid stockouts</li>
          <li><strong>In Transit:</strong> Once a Stock Transfer Order's source GIN is issued, that quantity moves into a single aggregated "In Transit" row until the destination GRN is received — it's already left the source location but hasn't landed at the destination yet</li>
        </ul>
      </div>
    )
  },

  // Inventory Module - Stock Transfer Orders
  'stock-transfer-orders': {
    title: 'Stock Transfer Orders',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Stock Transfer Orders let you create orders to move stock from one internal location to another—
          for example, from a warehouse to a retail store or production unit. You can initiate transfers
          between warehouses, retail stores, and production units.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Source & destination:</strong> Choose any combination of warehouse, retail store, or production unit</li>
          <li><strong>Line items:</strong> Specify items and quantities to transfer</li>
          <li><strong>Status tracking:</strong> Track orders as DRAFT, IN_TRANSIT, or COMPLETED</li>
          <li><strong>e-Way bill:</strong> When e-way bills are switched on, <em>e-Way</em> on a dispatched transfer raises one under your own GSTIN (a delivery challan, for own use), valued at the cost of the goods dispatched. A transfer to another state is a supply between GST registrations and needs a tax invoice instead.</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Open the <strong>Stock Transfer Orders</strong> tab under Inventory</li>
          <li>Click <strong>"+ Create Stock Transfer Order"</strong> and select source and destination locations</li>
          <li>Add line items (item, quantity, UOM) and save</li>
          <li>Use GIN at the source location and GRN at the destination to record actual stock movement when the transfer is executed</li>
        </ol>
      </div>
    )
  },

  // Purchase Module - Goods Receipt Notes (GRNs)
  'grns': {
    title: 'Goods Receipt Notes',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Goods Receipt Notes (GRN) are used to record the receipt of materials and services from vendors. 
          GRNs can be created from Purchase Orders or independently, and they update inventory stock levels 
          and create accounting transactions automatically.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Material Items:</strong> Record received quantities of materials/items</li>
          <li><strong>Lot tracking:</strong> Each material line becomes an inventory lot. An internal lot number is assigned automatically (YYYYMMDD-SEQ) if you leave it blank, so every receipt is traceable. A vendor lot number can be added optionally.</li>
          <li><strong>Expiry date:</strong> if your organization keeps expiry dates (Org Admin &rsaquo; Org settings &rsaquo; Expiry dates), each material line has an optional Expiry date. It can&apos;t be before the day you received the goods. When you issue stock, the lot that goes bad first is offered first, and an expired lot is flagged.</li>
          <li><strong>Service Items:</strong> Record services received along with materials</li>
          <li><strong>Linked Services:</strong> Services can be linked to material items with synchronized quantities</li>
          <li><strong>Weigh Bridge Integration:</strong> Link weigh bridge receipts to auto-populate quantities</li>
          <li><strong>Purchase Order Link:</strong> Create GRN from existing Purchase Orders</li>
          <li><strong>Procurement Survey Link:</strong> Receive against a field slip (movement type Receive against Procurement Survey)</li>
          <li><strong>Multi-vendor Support:</strong> Handle different vendors for materials and services</li>
          <li><strong>Goods billed before they came:</strong> If the vendor has a bill marked <em>goods to arrive</em>, the form asks which bill these goods are for. Choose it (and <em>Fill the lines from this bill</em> if you like): the goods go into stock and the vendor isn&apos;t owed for them again. Tick <em>More of this bill&apos;s goods are still to come</em> for a part delivery. If they&apos;re for none of those bills, choose <em>None of these bills</em>. A GRN on a bill can only be reversed by cancelling the bill.</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create GRN"</strong> to create a new Goods Receipt Note</li>
          <li>Select an inbound Goods Movement Type (e.g. Receive from Vendor, or Receive against Procurement Survey)</li>
          <li>Select a warehouse where goods are being received</li>
          <li>Select a Purchase Order or Procurement Survey (depending on movement type) to load line items</li>
          <li>Add material items with received quantities, unit prices, and vendors</li>
          <li>Add service items (linked or independent) as needed</li>
          <li>Link weigh bridge receipts to auto-populate material quantities</li>
          <li>Save the GRN - it will update stock levels and create ledger transactions</li>
        </ol>

        <h5 className="mt-4">Accounting Integration</h5>
        <p>
          When a GRN is saved, the system automatically creates ledger transactions:
        </p>
        <ul>
          <li>Debits the Warehouse Inventory Control ledger</li>
          <li>Credits the Vendor Accounts Payable ledger</li>
          <li>Updates ledger balances automatically</li>
        </ul>

        <h5 className="mt-4">Status & Corrections</h5>
        <ul>
          <li><strong>DRAFT / PARTIAL:</strong> Still editable. Stock is counted for PARTIAL; ledgers post only once the GRN is RECEIVED.</li>
          <li><strong>RECEIVED:</strong> Stock and ledgers have been posted. The GRN is now <strong>locked</strong> — it cannot be edited, cancelled or deleted.</li>
          <li><strong>Made a mistake on a RECEIVED GRN?</strong> Use the <strong>Reverse</strong> action. This creates contra ledger entries that exactly undo the original postings, removes the received stock, and marks the GRN as <strong>REVERSED</strong> (kept for audit). Then create a fresh, correct GRN.</li>
          <li>Reversal is blocked if any lot from the GRN has already been issued via a GIN — reverse or cancel those GINs first.</li>
          <li><strong>CANCELLED:</strong> Only a DRAFT GRN can be cancelled/deleted (nothing was posted).</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Use Purchase Orders to pre-populate GRN line items</li>
          <li>Link weigh bridge receipts for accurate weight-based quantities</li>
          <li>Linked service quantities automatically match their material item quantities</li>
          <li>Use "View & Print" to generate printable GRN documents</li>
          <li>The internal lot number is filled in automatically — you don't need to type one, but it guarantees issued materials can always be traced to a specific receipt</li>
          <li>Double-check quantities and prices before setting a GRN to RECEIVED — after that, corrections require a reversal</li>
        </ul>
      </div>
    )
  },

  // Affiliate screen (only affiliates see it; kept in the account managers' text, never the customers')
  'partner-affiliate': {
    title: 'Your referrals',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The Affiliate screen shows your referral link and code, the businesses that signed up with them, and what you have earned. If a business's payment is refunded, the commission it earned is taken back in proportion and shows as a negative line marked Refunded (if it had already been paid to you, it is taken off your next payout).
          You see only a business&apos;s name, when it signed up, whether it is paying and which plan: nothing else of theirs.
        </p>
        <h5 className="mt-4">How it works</h5>
        <ul>
          <li>Share your link or code. A business that uses it when it signs up is credited to you from that day.</li>
          <li>You earn a percentage of what it pays, at one rate for the first months and a lower one after, for the period shown under <strong>What you earn</strong>.</li>
          <li>It accrues each time a payment is recorded as paid, and shows as <strong>Waiting</strong> until iBigDo has paid it out, then <strong>Paid</strong>.</li>
        </ul>
        <h5 className="mt-4">If a business forgot your code</h5>
        <p>Tell iBigDo support which business it is; they can credit it to you by hand.</p>
      </div>
    )
  },

  // Settings - Two-step sign-in
  'two-step-sign-in': {
    title: 'Two-step sign-in',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Two-step sign-in asks for a 6-digit code from an authenticator app on your phone, as well as your password. Someone who
          guesses or steals your password still cannot get in without your phone. It is off until you turn it on for yourself in
          Settings, and an Org Admin can require it of everyone in the organization.
        </p>
        <h5 className="mt-4">Turning it on</h5>
        <ol>
          <li>Install an authenticator app on your phone: Google Authenticator, Microsoft Authenticator, Authy or any other that
            makes standard 6-digit codes.</li>
          <li>In Settings, under <strong>Two-step sign-in</strong>, click <strong>Turn on</strong> and scan the picture with the app
            (or type the key shown under it).</li>
          <li>Type the code the app shows. iBigDo then shows your <strong>recovery codes</strong>.</li>
        </ol>
        <h5 className="mt-4">Recovery codes</h5>
        <p>
          Eight one-time codes for a lost or broken phone: type one in place of the 6-digit code and it signs you in once. They are
          shown only when you turn two-step on or make new ones, so copy, download or print them and keep them somewhere safe. Settings
          shows how many are left and can make a fresh set (the old ones then stop working).
        </p>
        <h5 className="mt-4">Signing in</h5>
        <ul>
          <li>After your password, type the code from your app. Tick <strong>Don&apos;t ask for a code on this browser for 30 days</strong> on
            your own computer to skip it there. A new password, or <strong>Forget them</strong> in Settings, makes every browser ask again.</li>
          <li>Five wrong codes in a row stop the code step for 15 minutes.</li>
        </ul>
        <h5 className="mt-4">Lost your phone and your recovery codes?</h5>
        <p>
          Ask your Org Admin to open Org Admin &rarr; Users, edit your user and choose <strong>Reset two-step sign-in</strong>. If you are the
          only Org Admin, iBigDo support can reset it once they have checked it is you. You set it up again at your next sign-in if your
          organization requires it.
        </p>
        <h5 className="mt-4">For the Org Admin</h5>
        <ul>
          <li>Org Admin &rarr; Org settings &rarr; <strong>Two-step sign-in</strong> requires it of everyone. A person who has not set it up
            is walked through it at their next sign-in, and cannot turn it off afterwards.</li>
          <li>The Users list marks who has it on.</li>
          <li>Anyone already signed in stays signed in until their session ends (up to a day).</li>
        </ul>
      </div>
    )
  },

  // Purchase Module - Goods Issue Notes (GINs)
  'daily-summary': {
    title: 'Daily Summary Email',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          A short email each morning at 8 about the business yesterday and today, so you know where things stand without
          logging in. It is off until you turn it on for yourself in Settings, and only the Org Admin and the Finance
          Manager can, since it shows the money.
        </p>
        <h5 className="mt-4">What it covers</h5>
        <ul>
          <li><strong>Yesterday:</strong> sales (bills issued, including GST), money received from customers, paid to
            vendors, and expenses.</li>
          <li><strong>Cash and bank now:</strong> each cash and bank account&apos;s balance.</li>
          <li><strong>To collect:</strong> what customers owe, how much is overdue, and the customers most overdue.</li>
          <li><strong>To pay:</strong> what you owe vendors, what is overdue or due this week, and bills to micro and small
            (MSME) vendors close to the 45-day limit.</li>
          <li><strong>Waiting:</strong> items at or below their reorder point, purchase orders to approve, and lots
            awaiting a quality check.</li>
        </ul>
        <p>
          The figures are the same as on the Dashboard, Receivables and Payables. <em>See a sample</em> shows the email as
          it would go this morning; <em>Email me one now</em> sends it straight away. If you belong to several
          organizations, turn it on in each one you want.
        </p>
      </div>
    )
  },
  'stock-ageing': {
    title: 'Stock Ageing',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          What your stock is worth and how long it has been sitting. Every lot on hand is valued at its cost (purchase price
          plus freight and other costs on its receipt), the same way as the closing stock in your Profit &amp; Loss, and aged
          from the day it was received.
        </p>
        <h5 className="mt-4">Reading it</h5>
        <ul>
          <li><strong>By age:</strong> the value in each band, 0-30 days up to over 180 days. Stock in the older bands is
            money tied up: sell it, use it, or stop buying more of it.</li>
          <li><strong>Slow-moving:</strong> items that haven&apos;t gone out (sold, used in production, transferred or
            returned) for the days you choose. Stock written off doesn&apos;t count as going out.</li>
          <li><strong>Expiry:</strong> where your organization keeps expiry dates (Org Admin &rsaquo; Org settings), two more cards show what has expired and what goes bad within 30 days, and an Expires column shows each item&apos;s nearest date (red once past, amber within a month).</li>
          <li>Stock you already had when you switched expiry on has no dates yet: open an item and press <strong>Set date</strong> (or <strong>Change</strong>) on a lot. A line above the table says how many lots still have none.</li>
          <li>Click an item to see its lots, where each is and when it came in. Download the list as CSV for Excel.</li>
          <li>Choose a location to see one warehouse, store or production unit.</li>
        </ul>
        <p>Inventory and production managers and the Org Admin see this, since it shows values.</p>
      </div>
    )
  },
  'stock-adjustments': {
    title: 'Stock Adjustments',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The books&apos; stock only stays right if what goes missing, gets damaged or turns up is written down. Stock
          adjustments do that, one at a time or from a stock count, and the closing stock in your profit and loss moves with them.
        </p>

        <h5 className="mt-4">Writing stock off</h5>
        <ul>
          <li><strong>Write off stock</strong>: choose the location and why (damaged, expired, lost or stolen, given as samples,
            used in the business), then enter how much of each lot to take off. It is valued at that lot&apos;s cost.</li>
          <li><strong>Add stock found</strong>: for more than the books say. It comes in at the cost of the lot you enter it against, owed to nobody.</li>
        </ul>

        <h5 className="mt-4">Counting stock</h5>
        <ol>
          <li>Click <strong>Count sheet</strong>, choose the location and print it: every lot the books hold there, with a blank column to write in.</li>
          <li>Count, then click <strong>Stock count</strong> and enter what was on the shelf for each lot you counted. Lots left empty are not changed.</li>
          <li>Each line shows the difference and its value. <strong>Post</strong> writes off what is short and adds what is extra in one go.</li>
        </ol>

        <h5 className="mt-4">Good to know</h5>
        <ul>
          <li>Storekeepers see adjustments and print count sheets; inventory managers (and the Org Admin) post and undo them.</li>
          <li><strong>Undo</strong> puts the stock back as it was. Stock found can&apos;t be taken off again once some of it has been used.</li>
          <li>Each adjustment is a goods issue note (what went out) and a goods receipt note (what was found); they show in those
            lists, but change only through here.</li>
          <li>Lots still waiting for quality control are not listed until they pass.</li>
          <li>GST paid on goods lost, stolen, destroyed, written off or given as free samples can&apos;t be claimed: tell your
            accountant so the input tax credit is reversed.</li>
        </ul>
      </div>
    )
  },
  'gins': {
    title: 'Goods Issue Notes',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Goods Issue Notes (GIN) are used to record the issue or transfer of materials from a
          Warehouse, Retail Store, or Production Unit. GINs reduce inventory stock levels at the
          chosen issuing location and can be used for internal transfers, production issues, or
          material consumption.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Stock Reduction:</strong> Automatically reduces inventory quantities at the issuing location</li>
          <li><strong>Issuing Location:</strong> Issue materials from a specific Warehouse, Retail Store, or Production Unit</li>
          <li><strong>Mandatory Lot Selection:</strong> Each issued line must specify the inventory lot it is drawn from — the operator explicitly decides which lot to consume</li>
          <li><strong>Multiple Items:</strong> Issue multiple items in a single GIN</li>
          <li><strong>Remarks:</strong> Add notes about the reason for issue</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create GIN"</strong> to create a new Goods Issue Note</li>
          <li>Select the issuing location (Warehouse, Retail Store, or Production Unit)</li>
          <li>Add line items: choose the item, then <strong>select the inventory lot (required)</strong>, and enter <strong>Requested Qty</strong> (planned) and optionally <strong>Issue Qty</strong> (actual stock issued)</li>
          <li>Enter remarks if needed (e.g., reason for issue, destination)</li>
          <li>Save the GIN - it will update stock levels automatically</li>
        </ol>

        <h5 className="mt-4">Quantity fields</h5>
        <ul>
          <li><strong>Requested Qty:</strong> Planned quantity to issue. When linked to a Work Order, this defaults to remaining requirement (WO required − already issued on prior GINs). For a Stock Transfer Order, it is the STO line quantity. It is not the issuing location's available stock.</li>
          <li><strong>Issue Qty:</strong> Actual quantity taken from stock. If left blank, it defaults to Requested Qty. Against a Work Order, Issue Qty cannot exceed remaining WO requirement.</li>
          <li><strong>Available stock:</strong> A lot is required, so availability is shown as Available in the selected lot. The issuing location's total is used as an additional safety check — a lot can only be issued from the same location it was received into.</li>
          <li><strong>Work Order already issued:</strong> When a WO is selected, each line shows WO Required, Already Issued, and Remaining from prior GINs.</li>
        </ul>

        <h5 className="mt-4">Stock Impact</h5>
        <p>
          When a GIN is saved, the system automatically:
        </p>
        <ul>
          <li>Reduces stock levels for issued items at the selected issuing location</li>
          <li>Updates inventory records in real-time</li>
          <li>Maintains audit trail of all material movements</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Verify stock availability before creating a GIN</li>
          <li>Selecting the inventory lot is mandatory — the GIN cannot be saved until every issued line has a lot chosen</li>
          <li>GINs are blocked if Issue Qty exceeds the selected lot's availability (and the issuing location's total)</li>
          <li>Use remarks to document the purpose of material issue</li>
          <li>Purchase Order returns (Return to Vendor) always require a Warehouse issuing location</li>
          <li>Against a Work Order, you can issue from any location (multiple GINs from different locations are allowed)</li>
        </ul>
      </div>
    )
  },

  // Purchase Module - Vendor Types
  'vendor-types': {
    title: 'Vendor Types',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Vendor Types allow you to categorize vendors based on their business function or role. 
          Common vendor types include Suppliers, Harvesting & Transportation (H&T), Transporters, 
          and Service Providers. This categorization helps in filtering and managing vendors effectively.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Organization-specific:</strong> Each organization can define its own vendor types</li>
          <li><strong>Active/Inactive Status:</strong> Enable or disable vendor types as needed</li>
          <li><strong>Description:</strong> Add detailed descriptions for each vendor type</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create Vendor Type"</strong> to add a new vendor type</li>
          <li>Enter a name for the vendor type (e.g., "H&T", "Transporter", "Supplier")</li>
          <li>Add an optional description</li>
          <li>Set the active status (inactive types won't appear in dropdowns)</li>
          <li>Save the vendor type</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Create vendor types before adding vendors to ensure proper categorization</li>
          <li>Use descriptive names that clearly indicate the vendor's role</li>
          <li>Deactivate vendor types instead of deleting them to maintain historical data</li>
        </ul>
      </div>
    )
  },

  // Production Module - BOM
  'bom': {
    title: 'Bill of Materials (BOM)',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Bill of Materials (BOM) defines the recipe or formula for producing a finished good. 
          A BOM lists all the raw materials, components, and quantities needed to manufacture one unit 
          of the finished product. BOMs are essential for production planning and material requirement calculations.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Finished Good:</strong> The item that this BOM produces</li>
          <li><strong>Line Items:</strong> List of materials/components with quantities required</li>
          <li><strong>Wastage Percentage:</strong> Expected wastage for each material</li>
          <li><strong>Version Control:</strong> Track different versions of BOMs</li>
          <li><strong>Default BOM:</strong> Mark one BOM as default for a finished good</li>
          <li><strong>Production Unit:</strong> Optional link to specific production unit</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create BOM"</strong> to create a new Bill of Materials</li>
          <li>Enter BOM name and select the finished good item</li>
          <li>Add line items for each material/component needed</li>
          <li>Specify quantity required for the BOM base (primary output base quantity)</li>
          <li>Optionally set wastage percentage for each material</li>
          <li>Set as default BOM if this is the primary recipe</li>
          <li>Save the BOM - it will be available for work order creation</li>
        </ol>

        <h5 className="mt-4">BOM Line Items</h5>
        <p>
          Each line item in a BOM specifies:
        </p>
        <ul>
          <li><strong>Item:</strong> The raw material or component</li>
          <li><strong>Quantity:</strong> Amount needed for the BOM primary output base quantity (batch size)</li>
          <li><strong>Unit of Measurement:</strong> UOM for the quantity</li>
          <li><strong>Wastage Percentage:</strong> Expected wastage (optional)</li>
          <li><strong>Notes:</strong> Additional information (optional)</li>
        </ul>

        <h5 className="mt-4">Version Control</h5>
        <p>
          BOMs can have versions to track changes over time:
        </p>
        <ul>
          <li>Use version numbers (e.g., 1.0, 1.1, 2.0) to track BOM revisions</li>
          <li>Only one BOM can be marked as default for a finished good</li>
          <li>Inactive BOMs won't appear in work order creation</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Ensure all required materials are listed in the BOM</li>
          <li>Set realistic wastage percentages for accurate material planning</li>
          <li>Use default BOM for the most commonly used recipe</li>
          <li>Keep BOMs active and up-to-date for accurate production planning</li>
          <li>Use version numbers to track BOM changes over time</li>
        </ul>
      </div>
    )
  },

  // Production Module - Work Orders
  'work-orders': {
    title: 'Work Orders',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Work Orders are production orders that specify what to manufacture, how much to produce, 
          and where production will happen. Work orders use BOMs to automatically calculate material 
          requirements and track actual material consumption during production.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>BOM-based:</strong> Automatically calculates material requirements from BOM</li>
          <li><strong>Quantity Tracking:</strong> Track planned vs. actual production quantities</li>
          <li><strong>Material Consumption:</strong> Record actual materials consumed and wastage</li>
          <li><strong>Status Tracking:</strong> DRAFT, APPROVED, IN_PROGRESS, COMPLETED, CANCELLED</li>
          <li><strong>Priority Levels:</strong> LOW, NORMAL, HIGH, URGENT</li>
          <li><strong>Production Unit:</strong> Specify where production will happen</li>
          <li><strong>Multi-warehouse materials:</strong> Issue materials via GIN from any warehouse(s); the work order does not bind to a single warehouse</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create Work Order"</strong> to create a new work order</li>
          <li>Select a BOM (this determines what will be produced)</li>
          <li>Select the production unit</li>
          <li>Enter quantity to produce</li>
          <li>Set priority and dates (start date, completion date)</li>
          <li>Save the work order</li>
          <li>Material requirements are automatically calculated from the BOM</li>
          <li>Update consumed quantities and wastage as production progresses</li>
        </ol>

        <h5 className="mt-4">Work Order Status</h5>
        <ul>
          <li><strong>DRAFT:</strong> Work order is being prepared, can be edited or deleted</li>
          <li><strong>APPROVED:</strong> Work order is approved and ready for production</li>
          <li><strong>IN_PROGRESS:</strong> Production has started. Requires at least one GRN received at the Production Unit against this work order</li>
          <li><strong>COMPLETED:</strong> Production is finished. On completion: raw materials at the Production Unit are reduced by consumed + wastage, and finished goods are auto-received into the Production Unit (system GRN)</li>
          <li><strong>CANCELLED:</strong> Work order has been cancelled. Not allowed after any non-cancelled GIN or GRN exists against the work order</li>
        </ul>

        <h5 className="mt-4">Material Consumption</h5>
        <p>
          When a work order is created, line items are automatically generated from the BOM:
        </p>
        <ul>
          <li><strong>Required Quantity:</strong> Scaled from the BOM batch: BOM material qty × (work order qty ÷ primary output base qty), plus wastage %</li>
          <li><strong>Consumed Quantity:</strong> Actual materials consumed (updated during production)</li>
          <li><strong>Wastage Quantity:</strong> Actual wastage recorded</li>
        </ul>

        <h5 className="mt-4">Priority Levels</h5>
        <ul>
          <li><strong>LOW:</strong> Low priority production</li>
          <li><strong>NORMAL:</strong> Standard priority (default)</li>
          <li><strong>HIGH:</strong> High priority production</li>
          <li><strong>URGENT:</strong> Urgent production needs</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Ensure BOM is complete and accurate before creating work orders</li>
          <li>Update consumed quantities regularly during production</li>
          <li>Track wastage to improve BOM accuracy over time</li>
          <li>Use priority levels to manage production scheduling</li>
          <li>Only DRAFT and APPROVED work orders can be edited</li>
          <li>Move to IN_PROGRESS only after receiving materials at the Production Unit (GRN)</li>
          <li>Issue materials from any warehouse via GIN; multiple warehouses can supply one work order</li>
          <li>Completing a work order reduces raw material stock at the Production Unit (consumed + wastage) and creates finished goods stock there</li>
          <li>Cannot cancel a work order after goods have been issued or received against it</li>
          <li>Material requirements are calculated automatically from the BOM</li>
        </ul>
      </div>
    )
  },

  // Production Module - Products
  'products': {
    title: 'Products',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Products module allows you to manage your product catalog. You can create, edit, and delete products,
          track their pricing, stock quantities, and organize them by categories and brands.
        </p>
        <p>
          A product is something you sell. Every product is linked to one item in the <strong>Global Material Master</strong> (shared by every business on iBigDo, with the items you add yourself), the single
          list that purchase, stock, production and sales all use, so the same item can be bought, made or sold. It is not a
          &ldquo;raw material&rdquo; list: an item becomes a product here and a purchased material under Materials, and both point
          at the same entry.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Product Information:</strong> Store product name, SKU, description, and brand</li>
          <li><strong>Default sale price:</strong> Standard list price pre-filled when the product is added on Sales → Orders (editable per order)</li>
          <li><strong>Cost price:</strong> Internal cost for reference</li>
          <li><strong>Stock Tracking:</strong> Track stock quantities for inventory management</li>
          <li><strong>Categories:</strong> A product's category is its item's category, picked once when the item is made</li>
          <li><strong>Brand Management:</strong> Track product brands</li>
                  <li>If your online store is switched on, a product appears on it only once you publish it: tick it under <strong>Online Store &rarr; Products online</strong> (it needs a price). A product you add here is not on the store by itself.</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Below your products is the <strong>Global Material Master</strong>: search it by name, HSN or category and press <strong>+ Add</strong> on the item you sell. The product name fills in from it; change it only if you sell it under another name</li>
          <li>Not there? Press <strong>Create my own product</strong>: type one name, pick its category, and the item and the product are made together, ready to use at once. <strong>Save and add another</strong> keeps the form open for loading many</li>
          <li>Add the SKU, description and brand (optional)</li>
          <li>Set the default sale price (optional) and cost price (optional)</li>
          <li>Already hold stock of it? Tick <strong>I already have stock of this item</strong> and give the place, quantity and cost: it goes into your opening stock, so inventory matches</li>
          <li>Save the product</li>
          <li>To add <strong>photos</strong>, open the product with Edit: up to five (ten once your iBigDo uses the media bucket), from your files or the phone&apos;s camera. The first is the main photo; <strong>Make main</strong> changes it. The same dialog takes a <strong>video link</strong> (YouTube or Vimeo), which customers see as a Watch video button in the catalog you share</li>
          <li><strong>What makes a good photo:</strong> clear, on a plain background, at least 800 x 800 pixels, and square, because your online store and catalog show square pictures (a wide or tall photo has its edges cropped). A photo under 400 pixels on its shorter side is refused; one between 400 and 800 is accepted with a warning that it may look soft. JPG or PNG, up to 6 MB.</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Use SKU codes for easy product identification</li>
          <li>Maintain consistent category naming for better organization</li>
          <li>Update stock quantities regularly for accurate inventory tracking</li>
          <li>Set cost prices to track profit margins</li>
        </ul>
      </div>
    )
  },

  // Purchase Module - Vendors
  'vendors': {
    title: 'Vendors',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Vendors are suppliers, service providers, or transporters that your organization works with. 
          The Vendors module allows you to manage vendor information, contact details, payment terms, 
          and credit limits. Each vendor is automatically linked to an Accounts Payable ledger.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Vendor Types:</strong> Categorize vendors by type (Supplier, H&T, Transporter, etc.)</li>
          <li><strong>Contact Information:</strong> Store phone, email, and address details</li>
          <li><strong>GSTIN (India):</strong> For vendors in India, enter the 15-character GSTIN. It is checked, and the legal name and address are filled in from GST records. The vendor&apos;s state, and so whether their bills carry IGST or CGST + SGST, follows from it.</li>
          <li><strong>GST registration:</strong> Regular, Composition or Not registered. You can save a vendor without a GSTIN, but credit on their invoices can&apos;t be claimed until you add one. Composition vendors give no input credit.</li>
          <li><strong>Credit days and payment terms:</strong> Credit days set when their bills fall due (see Accounts &rarr; Payables); payment terms and credit limit are for reference</li>
          <li><strong>MSME (Udyam):</strong> Mark a micro, small or medium enterprise; bills from micro and small ones are due within 45 days and flagged in Payables</li>
          <li><strong>Bank Details:</strong> Store bank account information for payments</li>
          <li><strong>Accounts Payable:</strong> Automatic ledger creation for tracking liabilities</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create Vendor"</strong> to add a new vendor</li>
          <li>Enter vendor name (required)</li>
          <li>Select a vendor type (required)</li>
          <li>Enter phone number, city, state/province, and country (all required)</li>
          <li>Optionally fill in email, address, payment terms, and other details</li>
          <li>Save the vendor - an Accounts Payable ledger will be created automatically</li>
        </ol>

        <h5 className="mt-4">Accounts Payable Integration</h5>
        <p>
          When a vendor is created, the system automatically:
        </p>
        <ul>
          <li>Creates an Accounts Payable ledger for the vendor</li>
          <li>Tracks all transactions with the vendor</li>
          <li>Maintains liability balances automatically</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Ensure vendor types are created before adding vendors</li>
          <li>Complete vendor information for better record keeping</li>
          <li>Use credit limits to manage vendor relationships</li>
          <li>Vendor ledgers can be viewed in the Accounts module</li>
          <li>In View Transactions, click a reference number to open the GRN or payment document</li>
        </ul>
      </div>
    )
  },

  // Purchase Module - Purchase Orders
  'purchase-orders': {
    title: 'Purchase Orders',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Purchase Orders (PO) are formal documents sent to vendors to order materials and services. 
          POs define what items are being ordered, quantities, prices, and delivery terms. They serve 
          as the basis for creating Goods Receipt Notes (GRNs) when materials are received.
        </p>

        <h5 className="mt-4">Running low</h5>
        <p>
          Materials with a reorder point (Org Admin &rarr; Materials) show here, and on the Dashboard, once their stock is at or
          below it. Stock counts every location, less what open sale orders have reserved, plus what is already on an open
          purchase order (draft, approved or part-received), so nothing is ordered twice.
        </p>
        <ul>
          <li>The quantity to order is the preferred order quantity (POQ), or enough to get back to the reorder point plus the safety stock (SSQ); never below the minimum order quantity (MOQ). Change it before ordering.</li>
          <li>Tick the materials and click <strong>Make purchase order</strong>: the order form opens with each one at the vendor and rate it was last ordered at. Choose the warehouse (and a vendor for anything not bought before) and save.</li>
          <li>No reorder point set means the material is never listed.</li>
        </ul>

        <h5 className="mt-4">Sending it to the vendor</h5>
        <ul>
          <li>The print button shows the purchase order as the vendor gets it: your business and the vendor with GSTINs and states,
            where to deliver and by when, each item with its HSN, quantity, unit and rate, the total in words, the vendor&apos;s
            payment terms (from their record), your terms and a signature block. Print it or save it as a PDF.</li>
          <li>Once the order is approved, the green send button sends it to the vendor <strong>on WhatsApp or by email</strong>, with a
            link they open without logging in. The dialog shows when it was sent and opened; a link can be stopped.</li>
          <li>A PO with several vendors goes to each vendor separately, with only their lines: choose the vendor&apos;s copy in the print view.</li>
          <li>Set the terms printed on every purchase order (delivery hours, quality, billing) in Org Admin &rsaquo; Org settings.</li>
        </ul>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Material Items:</strong> Order materials/items with quantities and prices</li>
          <li><strong>Service Items:</strong> Order services that can be linked to material items</li>
          <li><strong>Linked Services:</strong> Services linked to materials have synchronized quantities</li>
          <li><strong>Multiple Vendors:</strong> Different vendors for materials and services</li>
          <li><strong>Last rate:</strong> once an item and vendor are chosen, the price starts at that vendor&apos;s rate on their last approved PO for it, shown under Unit Price with the PO and date</li>
          <li><strong>Status Tracking:</strong> Track PO status (DRAFT, APPROVED, COMPLETED)</li>
          <li><strong>GRN Creation:</strong> Create GRNs directly from Purchase Orders</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create Purchase Order"</strong> to create a new PO</li>
          <li>Select a warehouse where goods will be received</li>
          <li>Add material items with quantities, unit prices, and vendors</li>
          <li>Add service items (linked to materials or independent) as needed</li>
          <li>Set PO date and add remarks if needed</li>
          <li>Save the Purchase Order (starts as <strong>DRAFT</strong>)</li>
          <li>Use the <strong>Approve</strong> action on DRAFT POs to mark them as APPROVED</li>
          <li>Use "Create GRN" to receive goods against this PO</li>
        </ol>

        <h5 className="mt-4">Service Items</h5>
        <p>
          Purchase Orders support two types of service items:
        </p>
        <ul>
          <li><strong>Linked Services:</strong> Services linked to material items - quantities automatically match</li>
          <li><strong>Independent Services:</strong> Standalone services with their own quantities</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Create POs before receiving goods to maintain proper documentation</li>
          <li>Link services to materials when they are related (e.g., transportation for specific material)</li>
          <li>Only DRAFT and APPROVED POs (with no GRNs yet) can be edited</li>
          <li>Use "View & Print" to generate printable Purchase Order documents</li>
          <li>Use "View Transactions" to see GRNs with individual received line items and payments applied to the PO</li>
          <li>GRNs created from POs automatically load PO line items</li>
        </ul>
      </div>
    )
  },

  // Accounts Module - Ledgers
  'ledgers': {
    title: 'Ledgers',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Ledgers are the foundation of the accounting system. They represent accounts in your chart of accounts 
          and track financial transactions. Ledgers can be linked to vendors, warehouses, bank accounts, or 
          cash-in-hand locations, or can be standalone general ledger accounts.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Account Types:</strong> ASSET, LIABILITY, EQUITY, REVENUE, EXPENSE</li>
          <li><strong>Categories:</strong> Further classification within account types</li>
          <li><strong>Opening Balance:</strong> Set initial balance when creating a ledger</li>
          <li><strong>Current Balance:</strong> Automatically updated with transactions</li>
          <li><strong>Auto-created:</strong> Ledgers are automatically created for vendors, warehouses, banks, etc.</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create New Ledger"</strong> to create a manual ledger</li>
          <li>Enter ledger name and select account type</li>
          <li>Optionally select a category for better organization</li>
          <li>Set opening balance if applicable</li>
          <li>Save the ledger</li>
          <li>Use "View Transactions" to see all transactions for a ledger</li>
        </ol>

        <h5 className="mt-4">Auto-created Ledgers</h5>
        <p>
          The system automatically creates ledgers for:
        </p>
        <ul>
          <li><strong>Vendors:</strong> Accounts Payable ledgers (LIABILITY type)</li>
          <li><strong>Warehouses:</strong> Inventory Control ledgers (ASSET type)</li>
          <li><strong>Bank Accounts:</strong> Current Asset ledgers</li>
          <li><strong>Cash-in-Hand:</strong> Current Asset ledgers</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Use "Initialize General Ledger" to create standard chart of accounts</li>
          <li>Filter ledgers by type to focus on specific account categories</li>
          <li>View transactions to audit ledger activity</li>
          <li>Ledger balances update automatically when transactions are created</li>
        </ul>
      </div>
    )
  },

  // Accounts Module - Bank Accounts
  'bank-accounts': {
    title: 'Bank Accounts',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Bank Accounts module allows you to manage all bank accounts for your organization. 
          Each bank account is automatically linked to a Current Asset ledger that tracks the account balance 
          and all related transactions.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Account Details:</strong> Store account name, number, bank name, branch details</li>
          <li><strong>IFSC/SWIFT Codes:</strong> Store banking codes for transactions</li>
          <li><strong>Opening Balance:</strong> Set initial account balance</li>
          <li><strong>Current Balance:</strong> Automatically tracked via ledger</li>
          <li><strong>Auto-ledger:</strong> Current Asset ledger created automatically</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Add Bank Account"</strong> to create a new bank account</li>
          <li>Enter account name, bank name, and account number</li>
          <li>Add IFSC code (for Indian banks) or SWIFT code (for international)</li>
          <li>Enter branch details and address</li>
          <li>Set opening balance and currency</li>
          <li>Save - a Current Asset ledger will be created automatically</li>
        </ol>

        <h5 className="mt-4">Ledger Integration</h5>
        <p>
          When a bank account is created:
        </p>
        <ul>
          <li>A Current Asset ledger is automatically created</li>
          <li>Opening balance is set in the ledger</li>
          <li>All transactions update the ledger balance automatically</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Keep bank account information up to date</li>
          <li>Use the ledger to track all bank transactions</li>
          <li>Set correct opening balances for accurate financial reporting</li>
        </ul>
      </div>
    )
  },

  // Accounts Module - Bank Reconciliation
  'bank-reconciliation': {
    title: 'Bank Reconciliation',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Set your bank statement against your books. Upload the statement as your bank gives it; each line is matched to the
          receipt, payment or other entry it is, and you record what the books don&apos;t have yet (bank charges, interest, a
          receipt you hadn&apos;t entered). Then the reconciliation shows whether your books and the bank agree.
        </p>

        <h5 className="mt-4">Upload a statement</h5>
        <ol>
          <li>Download the statement from net banking as Excel (.xls or .xlsx) or CSV. Most banks&apos; layouts are read as they are: the lines about the account at the top and the totals at the bottom are skipped.</li>
          <li>Choose the bank account, click <strong>Upload statement</strong>, then <strong>Preview</strong>. Check the dates, the totals and which columns were read.</li>
          <li>Click <strong>Add</strong>. Lines already uploaded are skipped, so overlapping months are fine. Lines dated before your opening balances are set aside on their own.</li>
        </ol>

        <h5 className="mt-4">Matching</h5>
        <ul>
          <li>A line is matched on its own when an entry in the books has the same amount, in the same direction, within a few days, and no other line or entry fits as well; or when the entry&apos;s UTR or cheque number is in the narration.</li>
          <li>Where it isn&apos;t sure, the line shows likely entries: click <strong>Match</strong> on the right one.</li>
          <li>A cheque can take weeks to clear: an entry up to 45 days before the line can still be matched.</li>
          <li><strong>Unmatch</strong> undoes a match. <strong>Match again</strong> retries the automatic matching, for example after you record receipts elsewhere.</li>
        </ul>

        <h5 className="mt-4">Lines the books don&apos;t have</h5>
        <p>Click <strong>Record…</strong> and say what it is:</p>
        <ul>
          <li><strong>Receipt from a customer</strong> (Org Admin): records the receipt on that date, settling the customer&apos;s oldest unpaid bills first. If they paid a round figure (11,826 on a bill of 11,826.47), tick <em>settle the difference as a discount allowed</em>: a shortfall of 1 or less closes the bill, and the paise go to a Discount Allowed account, so the bank and the customer&apos;s statement both still agree. Cancelling the receipt gives it back.</li>
          <li><strong>Payment to a vendor</strong>: records the payment on account. If TDS applies to it, record it under Vendor Payments instead and match it here.</li>
          <li><strong>Bank charges</strong>, <strong>Interest received</strong>, <strong>Interest paid</strong>: posted to ledgers of those names (made the first time).</li>
          <li><strong>Another account</strong>: a cash withdrawal (Cash in Hand), a transfer to your other bank account, a loan, an expense.</li>
          <li><strong>Set it aside</strong>: the books need nothing for it.</li>
        </ul>
        <p>An entry made here can be undone with <strong>Undo entry</strong> (a reversing entry is posted). A receipt or payment recorded here stays when unmatched; cancel it where it lives if it was wrong.</p>

        <h5 className="mt-4">The reconciliation</h5>
        <p>
          The balance in your books, less deposits the bank hasn&apos;t credited yet, plus cheques it hasn&apos;t paid yet, plus
          and less statement lines not in your books yet, should be the bank&apos;s balance. When it is, you see
          <strong> Reconciled</strong>. A difference usually means an entry dated before the first statement line, a missing
          statement, or a wrong amount in the books. Entries before the first uploaded line are taken as cleared.
        </p>
      </div>
    )
  },

  // Accounts Module - Cash-in-Hand
  'cash-in-hand': {
    title: 'Cash-in-Hand',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Cash-in-Hand module allows you to manage physical cash locations within your organization. 
          Each cash location (e.g., Main Office Cash, Branch Cash) is tracked separately with its own 
          ledger account for accurate cash management.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Location Management:</strong> Track multiple cash locations</li>
          <li><strong>Address Details:</strong> Store location address and contact information</li>
          <li><strong>Opening Balance:</strong> Set initial cash balance for each location</li>
          <li><strong>Current Balance:</strong> Automatically tracked via ledger</li>
          <li><strong>Auto-ledger:</strong> Current Asset ledger created automatically</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Add Cash Location"</strong> to create a new cash location</li>
          <li>Enter location name (e.g., "Main Office Cash", "Branch Cash")</li>
          <li>Add location description and address details</li>
          <li>Set opening balance and currency</li>
          <li>Save - a Current Asset ledger will be created automatically</li>
        </ol>

        <h5 className="mt-4">Ledger Integration</h5>
        <p>
          When a cash location is created:
        </p>
        <ul>
          <li>A Current Asset ledger is automatically created</li>
          <li>Opening balance is set in the ledger</li>
          <li>All cash transactions update the ledger balance automatically</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Create separate cash locations for different physical locations</li>
          <li>Use the ledger to track all cash transactions</li>
          <li>Regularly reconcile cash balances with physical counts</li>
        </ul>
      </div>
    )
  },

  // Accounts Module - Expenses
  'transfers': {
    title: 'Transfers & Journals',
    content: (
      <div>
        <h5>Moving money between cash and bank</h5>
        <p>
          When cash is deposited in the bank, cash is drawn from the bank, or money moves from one bank account to another,
          record it here in one line: the date, from, to and the amount. Both balances move, the dashboard&apos;s cash and
          bank figure stays right, and the deposit matches its line on the bank statement in Bank Reconciliation.
        </p>

        <h5 className="mt-4">Journal entries (Finance Manager and Org Admin)</h5>
        <p>For what no other screen records. Each line is an account with a debit or a credit; the two totals must be equal.</p>
        <ul>
          <li><strong>Owner brings in capital:</strong> debit the bank, credit Capital Account.</li>
          <li><strong>Owner takes money out:</strong> debit Drawings, credit cash or bank.</li>
          <li><strong>Loan received:</strong> debit the bank, credit Loans. Repaying it is the other way round.</li>
          <li><strong>Year-end depreciation:</strong> debit Depreciation, credit Fixed Assets.</li>
          <li><strong>A correction your accountant asks for</strong>, such as reversing GST input credit on goods given as samples.</li>
        </ul>
        <p>
          Capital Account, Drawings, Loans, Fixed Assets and Depreciation are made the first time you use them; other
          accounts are added under Ledgers. Customers&apos; accounts aren&apos;t offered (use receipts and credit notes), nor
          stock (use Inventory &rsaquo; Stock Adjustments).
        </p>

        <h5 className="mt-4">Mistakes</h5>
        <p>
          Nothing here is edited. <strong>Undo</strong> posts the reverse on the same date, so the books show both. Anyone in
          finance can undo a transfer; a journal entry is undone by the Finance Manager or the Org Admin.
        </p>
        <p>Both appear in the Tally export: transfers as Contra vouchers, journal entries as Journal vouchers.</p>
      </div>
    )
  },
  'expenses': {
    title: 'Expenses',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Record what you pay on the spot from cash or bank (rent, electricity, wages, freight, repairs, tea) in one entry. No
          vendor or bill is needed. The expense shows on the Profit &amp; Loss, and the cash or bank balance goes down, on the day
          you paid.
        </p>

        <h5 className="mt-4">Record an expense</h5>
        <ol>
          <li>Pick the date, what it was for, the amount and the cash or bank account it was paid from (the last one you used is remembered).</li>
          <li>Add who it was paid to, a note or a receipt number if you like, and click <strong>Record expense</strong>. The form stays open for the next one.</li>
        </ol>
        <p>
          The usual heads (Rent, Electricity and water, Wages and salaries, Freight and transport, Repairs, Office and stationery,
          Fuel and travel, Tea and refreshments, Telephone and internet, Miscellaneous) become accounts the first time you use
          them. Any other expense account you have in Ledgers is offered too.
        </p>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>A bill with GST from a registered supplier (a GST rent invoice, say) goes in Purchase &rarr; Purchase Invoices instead, so the GST can be claimed.</li>
          <li>Something bought on credit, to be paid later, is a purchase bill or non-GST slip, so it shows in Payables until paid.</li>
          <li>Entered by mistake? <strong>Undo</strong> posts the reverse on the same day; the entry stays in the list, struck through.</li>
          <li>Paid from the bank? It matches its line in Bank Reconciliation on its own.</li>
        </ul>
      </div>
    )
  },

  // Accounts Module - Payables
  'payables': {
    title: 'Payables',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          What you owe each vendor, and when. It is worked out from the books, so the totals agree with the Balance Sheet,
          and split into what is overdue, due in the next 7 days, and due later.
        </p>

        <h5 className="mt-4">How it works</h5>
        <ul>
          <li>Each vendor bill (and non-GST slip) falls due its vendor&apos;s <strong>credit days</strong> after the bill date (set them on the vendor). A different due date can be entered on the bill.</li>
          <li>When you record a vendor payment, choose the bills it pays: it fills in the oldest first, and you can change the amounts. Money not put against a bill (an advance, an older payment, TDS) settles the oldest bills.</li>
          <li>Return notes against a bill take it down.</li>
          <li><strong>Not on a bill</strong> is owed for goods received without a bill, or from your opening balances.</li>
          <li><strong>Paid ahead</strong> means you have paid the vendor more than their bills.</li>
        </ul>

        <h5 className="mt-4">Micro and small enterprises (MSME)</h5>
        <p>
          A vendor registered as a micro or small enterprise (Udyam) must be paid within 45 days of the bill, or within 15 days
          when no credit period was agreed. Since 2024, an amount paid to them later can&apos;t be claimed as an expense for income
          tax that year. Mark such vendors on the vendor form; their bills then carry a &ldquo;pay by&rdquo; date and are flagged
          here as it nears.
        </p>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Click a vendor to see their open bills, print them, or record a payment.</li>
          <li>Turn on <strong>Only overdue or due this week</strong> to plan this week&apos;s payments.</li>
        </ul>
      </div>
    )
  },

  // Accounts Module - Vendor Payments
  'vendor-payments': {
    title: 'Vendor Payments',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Vendor Payments module allows you to record and manage payments made to vendors. The system automatically 
          creates journal entries to maintain accurate accounting records. Payments can be applied to specific Purchase 
          Orders (POs), recorded as advance payments, or used to settle outstanding PO balances.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Who can use it:</strong> The organization admin and the finance roles (Finance Operator and Finance Manager)</li>
          <li><strong>Payment Recording:</strong> Record payments with date, amount, and payment method</li>
          <li><strong>Multiple Payment Methods:</strong> Support for Check, ACH, Wire Transfer, Cash, Card, and Other</li>
          <li><strong>Source Account Selection:</strong> Fund payments from any Current Asset account (Bank Account or Cash-in-Hand)</li>
          <li><strong>Bills it pays:</strong> The vendor&apos;s open bills are listed with their due dates; the payment is put against the oldest first, and you can change the amounts. See Payables.</li>
          <li><strong>PO Application:</strong> Apply payments to one or more Purchase Orders</li>
          <li><strong>Outstanding Balance Display:</strong> View vendor's total outstanding balance when selecting a vendor</li>
          <li><strong>Automatic Journal Entries:</strong> System creates double-entry bookkeeping transactions automatically</li>
          <li><strong>Payment Types:</strong> ADVANCE (not linked to PO), PO_PAYMENT (fully applied), SETTLEMENT (partial application)</li>
          <li><strong>Cancelling a payment:</strong> A payment recorded by mistake (wrong amount or vendor) can't be edited, but the Org Admin or Finance Manager can <strong>Cancel</strong> it with a reason: its entries, and any TDS kept back, are reversed today, and the bills and purchase orders it paid are open again. It can't be cancelled once its TDS is deposited (undo the deposit first) or while it is matched to a bank statement line (unmatch it first).</li>
          <li><strong>TDS (only if your organization deducts it):</strong> for a vendor with a TDS section, the TDS to keep back is worked out
            as you type the amount; the bank pays the rest and the vendor is settled in full. You can change the figure (for a
            lower-deduction certificate, say). See the TDS tab.</li>
        </ul>

        <h5 className="mt-4">How to Record a Payment</h5>
        <ol>
          <li>Click <strong>"+ Record Payment"</strong> button</li>
          <li><strong>Select Vendor:</strong> Choose the vendor from the dropdown. The outstanding balance will be displayed</li>
          <li><strong>Enter Payment Details:</strong>
            <ul>
              <li>Payment Date (defaults to today)</li>
              <li>Payment Amount (must be greater than zero)</li>
              <li>Payment Method (Check, ACH, Wire, Cash, Card, or Other)</li>
              <li>Source Account (select a Current Asset account - Bank Account or Cash-in-Hand)</li>
              <li>Reference Number (optional - check number, transaction ID, etc.)</li>
            </ul>
          </li>
          <li><strong>Bills this pays:</strong> the vendor&apos;s open bills, oldest due first, filled in from the payment amount. Change the amounts to pay particular bills; what isn&apos;t put against a bill settles the oldest ones.</li>
          <li><strong>Apply to Purchase Orders (Optional):</strong>
            <ul>
              <li>If the vendor has open POs, a table will appear showing PO details</li>
              <li>Enter the amount to apply to each PO in the "Apply Amount" column</li>
              <li>You can apply the payment to multiple POs</li>
              <li>Leave blank to record as an advance payment</li>
            </ul>
          </li>
          <li><strong>Add Notes (Optional):</strong> Enter any additional information about the payment</li>
          <li>Click <strong>"Record Payment"</strong> to save</li>
        </ol>

        <h5 className="mt-4">Payment Types Explained</h5>
        <ul>
          <li><strong>ADVANCE:</strong> Payment not linked to any Purchase Order. Used for prepayments or advance payments for future orders</li>
          <li><strong>PO_PAYMENT:</strong> Payment fully applied to one or more Purchase Orders. The total applied amount equals the payment amount</li>
          <li><strong>SETTLEMENT:</strong> Partial payment applied to PO(s). Some amount remains unapplied or is an advance</li>
        </ul>

        <h5 className="mt-4">Journal Entry Creation</h5>
        <p>
          When a payment is recorded, the system automatically creates a journal entry:
        </p>
        <ul>
          <li><strong>Debit:</strong> Vendor's Accounts Payable ledger (reduces liability)</li>
          <li><strong>Credit:</strong> Source Account ledger (reduces Current Asset)</li>
          <li>Both ledger balances are updated automatically</li>
          <li>The transaction is linked to the payment for audit trail</li>
        </ul>

        <h5 className="mt-4">Viewing Payments</h5>
        <p>
          The payment list shows all recorded payments with:
        </p>
        <ul>
          <li>Payment number (auto-generated: VP-YYYY-MM-XXXX)</li>
          <li>Payment date and vendor name</li>
          <li>Payment amount and method</li>
          <li>Source account used for the payment</li>
          <li>Payment type badge (ADVANCE, PO_PAYMENT, or SETTLEMENT)</li>
          <li>Applied PO(s) with amounts</li>
          <li>Payment status (RECORDED or CANCELLED)</li>
        </ul>

        <h5 className="mt-4">Understanding Outstanding Balance</h5>
        <p>
          The outstanding balance shown when selecting a vendor represents:
        </p>
        <ul>
          <li>Total amount owed to the vendor (Accounts Payable balance)</li>
          <li>Calculated from GRNs (goods/services received) minus payments made</li>
          <li>Includes all unpaid Purchase Order amounts</li>
        </ul>

        <h5 className="mt-4">Purchase Order Application</h5>
        <p>
          When applying payments to POs:
        </p>
        <ul>
          <li>Only open POs with outstanding balances are shown</li>
          <li>Each PO displays: PO number, date, total amount, paid amount, and outstanding balance</li>
          <li>You can apply any amount up to the outstanding balance</li>
          <li>The system validates that applied amounts don't exceed outstanding balances</li>
          <li>You can apply the payment to multiple POs in a single transaction</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Always verify the outstanding balance before recording a payment</li>
          <li>Use reference numbers (check numbers, transaction IDs) for easy reconciliation</li>
          <li>Apply payments to POs when possible for better tracking and reporting</li>
          <li>Review the payment list regularly to track payment history</li>
          <li>All payments are immutable - ensure accuracy before recording</li>
          <li>Use notes field to document special circumstances or agreements</li>
          <li>Source account must be a Current Asset (Bank Account or Cash-in-Hand ledger)</li>
        </ul>

        <h5 className="mt-4">Best Practices</h5>
        <ul>
          <li>Record payments promptly after they are made</li>
          <li>Match payments to invoices/GRNs when possible</li>
          <li>Maintain consistent reference numbering for easy tracking</li>
          <li>Regularly reconcile payment records with bank statements</li>
          <li>Use advance payments sparingly and track them carefully</li>
        </ul>
      </div>
    )
  },

  // Org Admin Module - Overview
  'overview': {
    title: 'Overview',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The Overview tab provides a dashboard view of key organization statistics including total users, 
          active users, and total roles. This gives you a quick snapshot of your organization's user management status.
        </p>

        <h5 className="mt-4">Key Metrics</h5>
        <ul>
          <li><strong>Total Users:</strong> Count of all users in the organization</li>
          <li><strong>Active Users:</strong> Count of users with active status</li>
          <li><strong>Total Roles:</strong> Number of roles defined in the organization</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <p>
          The Overview tab displays automatically when you access the Org Admin module. 
          Use the navigation cards or tabs to access detailed management features for users, roles, 
          warehouses, production units, and organization settings.
        </p>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Monitor user counts to track organization growth</li>
          <li>Check active vs total users to identify inactive accounts</li>
          <li>Use other tabs to manage detailed settings</li>
        </ul>
      </div>
    )
  },

  // Org Admin Module - Users
  'users': {
    title: 'Users',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The Users tab allows Organization Administrators to manage all users within their organization. 
          You can create new users, edit existing users, activate/deactivate accounts, and assign roles.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>User Creation:</strong> Add new users to the organization</li>
          <li><strong>User Management:</strong> Edit user details, activate/deactivate accounts</li>
          <li><strong>Role Assignment:</strong> Assign roles to users for access control</li>
          <li><strong>Status Management:</strong> Enable or disable user accounts</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create User"</strong> and enter their email first</li>
          <li>If they already use iBigDo (with another business), you&apos;re told so: click <strong>Add to organization</strong>. They sign in
            with their own password, and their name and phone stay as they have them.</li>
          <li>If they are new, enter their name and phone. By default we email them a link to choose their own password (it works once,
            for 7 days), so you never need to know it. Or choose <strong>Set a password now</strong> and give it to them yourself.</li>
          <li>Every password needs at least 10 characters with a capital letter, a small letter and a number, and cannot be a common one
            like Password123 or contain the person&apos;s email. The form shows what is still missing as you type.</li>
          <li>Assign their roles</li>
          <li>Lost or expired link? Open the user with Edit and click <strong>Email a set-password link</strong>.</li>
          <li>On each row, the buttons are: <strong>Roles</strong> (the badge icon: what this person can do), <strong>View &amp; Print</strong>, <strong>Edit</strong>, and <strong>Remove from this organization</strong> (the bin; you can&apos;t remove yourself, and an organization always keeps one Org Admin)</li>
        </ol>
        <p>
          The list shows each person&apos;s roles, whether they are active (and <strong>2-step</strong> when they sign in with a code from an authenticator app), and
          their <strong>last sign-in</strong>. <strong>Not yet</strong> means they have not signed in, so check they got the invitation and email a new link from Edit if not.
        </p>

        <h5 className="mt-4">User Roles</h5>
        <p>
          Users must be assigned roles to access system features:
        </p>
        <ul>
          <li>Roles define what permissions a user has</li>
          <li>Users can have multiple roles</li>
          <li>Role permissions are managed in the Roles tab</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Keep user information up to date</li>
          <li>Deactivate instead of deleting users to maintain audit trails</li>
          <li>Assign appropriate roles based on user responsibilities</li>
        </ul>
      </div>
    )
  },

  // Org Admin Module - Roles
  'roles': {
    title: 'Roles',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The Roles tab allows you to define and manage roles within your organization. Roles are collections 
          of permissions that determine what users can access and do in the system. Users are assigned roles 
          to grant them appropriate access levels.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Role Creation:</strong> Create custom roles for your organization</li>
          <li><strong>Permission Assignment:</strong> Assign specific permissions to roles</li>
          <li><strong>User Assignment:</strong> Assign roles to users</li>
          <li><strong>Role Management:</strong> Edit or delete roles as needed</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create Role"</strong> to create a new role</li>
          <li>Enter role name and description</li>
          <li>Select permissions from the available list</li>
          <li>Save the role</li>
          <li>Assign the role to users in the Users tab</li>
        </ol>

        <h5 className="mt-4">Permissions</h5>
        <p>
          Permissions control access to system features:
        </p>
        <ul>
          <li>Permissions are defined by System Administrators</li>
          <li>Select only necessary permissions for each role</li>
          <li>Follow principle of least privilege</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Create roles that match job functions in your organization</li>
          <li>Review and update role permissions regularly</li>
          <li>Test roles before assigning to multiple users</li>
        </ul>
      </div>
    )
  },

  // Org Admin Module - Retail Stores
  'retail-stores': {
    title: 'Retail Stores',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Retail Stores represent locations where your organization sells goods to retail customers. 
          Each retail store has address and contact details, similar to warehouses, for consistent 
          location management across your organization.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Location Management:</strong> Store retail store address and contact details</li>
          <li><strong>Address Fields:</strong> Address, city, district, state/province, country, postal code</li>
          <li><strong>Contact Info:</strong> Phone number and email</li>
          <li><strong>Status Management:</strong> Activate or deactivate retail stores</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create Retail Store"</strong> to add a new retail store</li>
          <li>Enter store name and description</li>
          <li>Add complete address details (address, city, district, state, country, postal code)</li>
          <li>Add contact information (phone, email)</li>
          <li>Set active status and save</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Use descriptive names that identify each store location</li>
          <li>Keep store information updated for accurate records</li>
          <li>Deactivate instead of deleting to maintain historical data</li>
        </ul>
      </div>
    )
  },

  // Org Admin Module - Warehouses
  'warehouses': {
    title: 'Warehouses',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Warehouses represent physical storage locations where inventory is stored. Each warehouse has its own 
          inventory tracking and is automatically linked to an Inventory Control ledger for accounting purposes.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Location Management:</strong> Store warehouse address and contact details</li>
          <li><strong>Inventory Tracking:</strong> Each warehouse tracks its own stock levels</li>
          <li><strong>Auto-ledger:</strong> Inventory Control ledger created automatically</li>
          <li><strong>Status Management:</strong> Activate or deactivate warehouses</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create Warehouse"</strong> to add a new warehouse</li>
          <li>Enter warehouse name and description</li>
          <li>Add complete address details (address, city, state, country, postal code)</li>
          <li>Add contact information (phone, email)</li>
          <li>Set active status</li>
          <li>Save - an Inventory Control ledger will be created automatically</li>
        </ol>

        <h5 className="mt-4">Ledger Integration</h5>
        <p>
          When a warehouse is created:
        </p>
        <ul>
          <li>An Inventory Control ledger (ASSET type) is automatically created</li>
          <li>All GRNs and GINs update this ledger</li>
          <li>Ledger balance reflects inventory value in the warehouse</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Create warehouses before setting up inventory operations</li>
          <li>Use descriptive names that clearly identify locations</li>
          <li>Keep warehouse information updated</li>
          <li>Deactivate instead of deleting to maintain historical data</li>
        </ul>
      </div>
    )
  },

  // Org Admin Module - Production Units
  'production-units': {
    title: 'Production Units',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Production Units represent manufacturing or processing facilities within your organization. 
          Similar to warehouses, production units have location details and can be used to track production 
          activities and material consumption.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Location Management:</strong> Store production unit address and contact details</li>
          <li><strong>Status Management:</strong> Activate or deactivate production units</li>
          <li><strong>Organization-specific:</strong> Each organization manages its own production units</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create Production Unit"</strong> to add a new production unit</li>
          <li>Enter production unit name and description</li>
          <li>Add complete address details (address, city, state, country, postal code)</li>
          <li>Add contact information (phone, email)</li>
          <li>Set active status</li>
          <li>Save the production unit</li>
        </ol>

        <h5 className="mt-4">Use Cases</h5>
        <p>
          Production Units can be used for:
        </p>
        <ul>
          <li>Tracking manufacturing facilities</li>
          <li>Managing processing locations</li>
          <li>Organizing production-related data</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Use descriptive names that identify the production facility</li>
          <li>Keep production unit information updated</li>
          <li>Deactivate instead of deleting to maintain historical records</li>
        </ul>
      </div>
    )
  },

  // Org Admin Module - Settings
  'settings': {
    title: 'Organization Settings',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Organization Settings allows you to manage your organization's profile, contact information, 
          and default currency. These settings affect how the system displays information and processes 
          transactions throughout the application.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Organization Profile:</strong> Update organization name, description, contact details</li>
          <li><strong>Address Information:</strong> Manage organization address</li>
          <li><strong>Default Currency:</strong> Set the default currency for financial transactions</li>
          <li><strong>Expiry dates:</strong> switch on if your stock goes bad (food, chemicals, medicines). Goods receipts then ask for each lot&apos;s expiry date, Stock Ageing shows what is expiring, and the Dashboard and Inventory warn about lots past their date or going bad within 30 days. Off by default; when off, nothing about expiry shows.</li>
          <li><strong>Date format:</strong> How dates are shown and typed for everyone in your organization: day first (18/10/2026), month first (10/18/2026) or year first (2026-10-18). Left on the default, it follows your country: day first for India. Documents that spell the month, such as 18 Oct 2026, read the same either way.</li>
          <li><strong>Logo URL:</strong> Set organization logo for branding</li>
          <li><strong>GST Tax Profile (India):</strong> Your GSTIN, legal and trade name, PAN and registration type. They print on tax invoices as the supplier and decide CGST + SGST versus IGST. Set your State above as well.</li>
          <li><strong>e-Invoice and e-Way bill:</strong> Under the GST Tax Profile, switch these on (needs your GSTIN), set the date e-invoicing starts for you and the value above which an e-way bill is needed, and enter your GST API login. The login is stored encrypted. Switch e-invoicing on only once your turnover crosses the limit that applies to you. If your turnover is Rs 10 crore or more, tick the 30-day box: the portal refuses invoices and notes more than 30 days old, so each document then shows the date it must be registered by.</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Navigate to the Settings tab</li>
          <li>Review current organization settings</li>
          <li>Update any fields that need changes</li>
          <li>Click <strong>"Save Settings"</strong> to apply changes</li>
        </ol>

        <h5 className="mt-4">Default Currency</h5>
        <p>
          The default currency affects:
        </p>
        <ul>
          <li>Currency display in financial reports</li>
          <li>Default currency for new bank accounts and cash locations</li>
          <li>Currency formatting throughout the application</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Keep organization information current and accurate</li>
          <li>Set the correct default currency for your region</li>
          <li>Update settings when organization details change</li>
        </ul>
      </div>
    )
  },

  // Org Admin - Organization Materials
  'organization-materials': {
    title: 'Organization Materials',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Organization Materials lets you extend items from the global Material Master with
          organization-specific inventory attributes such as SKU, reorder parameters, and unit of
          measurement. This is where you make raw materials available for purchasing, production
          BOMs, and inventory operations in your organization.
        </p>

        <p>
          When you made your own material because you could not find it, iBigDo reviews it. The outcome (approved and kept private to you, added to the
          shared Global Material Master, joined with a shared item that is the same thing, or removed) shows at the top of this page as a note, with any
          category or HSN change, until you press <strong>Dismiss</strong>. Org Admin only.
        </p>

        <p>
          A size, brand or model of something general can be filed under a parent item: when you create or edit your own material, <strong>Parent item</strong> lets you pick
          the parent item (for example <em>Round Pad Lock, 50mm</em> under <em>Padlock</em>). It takes the same category as the parent item, and stays a separate material with its own stock, cost and reorder levels.
          The quickest way is <strong>Make a variant…</strong> on a row of the Global Material Master list below: it opens the form already filled in from that item
          (category, HSN, description and specification), filed under it. Give it its own name, such as <em>Padlock, 50 mm</em>, and change what differs.
          Use <strong>+ Add</strong> instead when the shared item is right as it is. A variant shows "filed under" its parent in your materials and in the list, the parent shows "your variants", and searching for the parent&apos;s name finds its variants.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Item Selection:</strong> Link to items from the Global Material Master (kept by iBigDo)</li>
          <li><strong>SKU:</strong> Organization-specific stock keeping unit</li>
          <li><strong>MOQ:</strong> Minimum order quantity when purchasing</li>
          <li><strong>SSQ:</strong> Safety stock quantity to maintain</li>
          <li><strong>ROP:</strong> Reorder point — at or below this stock the material shows as running low on the Dashboard and Purchase Orders</li>
          <li><strong>POQ:</strong> Purchase order quantity — standard order size</li>
          <li><strong>UOM:</strong> Stocking unit of measurement for this material (inventory is kept in this UOM)</li>
          <li><strong>Opening stock:</strong> Tick <em>I already have stock of this item</em> to enter what you hold now: where it is, how much, its cost per unit and, if you use them, a lot number. It is booked as your opening stock, the same as under Accounts &rsaquo; Opening Balances, so stock levels and the books agree from the first day. The date it is counted as at is asked once, for all your opening balances. Org Admin and Finance Manager see this.</li>
          <li><strong>GST rate:</strong> The item&apos;s usual GST rate. Sale bill lines (for products made from it) and purchase bill lines pick it up on their own; you can still change it on a line. Leave it empty if the rate varies.</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Below your materials, <strong>Add from the Global Material Master</strong> lists the materials shared by every business on iBigDo. Search by name, HSN code or category (several words narrow it down), pick a category to see everything under it, and move through the pages. Ones you already have are hidden unless you untick <em>Hide ones I already have</em></li>
          <li>Press <strong>+ Add</strong> on a row. The form opens with that item chosen: review its description and attribute values, then enter your own SKU and inventory parameters (MOQ, SSQ, ROP, POQ) as needed</li>
          <li>Select the stocking unit of measurement (e.g. Liter). GRN/GIN may use a compatible unit (e.g. mL); quantities are converted automatically</li>
          <li>Save — the material will be available in Purchase, Production, and Inventory modules. Adding many? Use <strong>Save and add another</strong>: the dialog stays open and empties, keeping where your opening stock is held, and you carry on with the next material</li>
        </ol>

        <h5 className="mt-4">Can&apos;t find it? Make your own</h5>
        <p>
          Check the list first. If the material is not there, click <strong>Can&apos;t find it? Create a new draft material</strong>:
          it is ready to use straight away, and only your organization sees it.
        </p>
        <ul>
          <li><strong>Category:</strong> pick one, or <strong>+ New category</strong> for your own (for example Metal Sheet).</li>
          <li><strong>Specification:</strong> what tells one item of a category from another, like Thickness or Grade. For a category you made, <strong>Manage attributes</strong> adds them (text, number, a pick-list, yes/no or date, with an optional unit such as mm); then fill them in on each item. For a shared category they are fixed.</li>
          <li><strong>Names:</strong> write them like &quot;MS Sheet 18 gauge&quot;. Extra spaces are fixed for you; a name all in capitals or with no capital is refused, and so is one that repeats a name already in the list.</li>
          <li>Once an item has a value for an attribute, the attribute&apos;s kind stays as it is and a pick-list option in use cannot be removed; you can still rename it, add options or switch it off.</li>
          <li>An item you made can be edited under its details. If iBigDo makes it available to everyone, it stays in your list but is no longer yours to edit: ask support to change it.</li>
        </ul>

        <h5 className="mt-4">Relationship to Material Master</h5>
        <p>
          Shared items (name, type, description, and catalog attributes such as variety or grade)
          come from System Admin → Material Master. Organization Materials adds your org&apos;s
          operational attributes on top. Materials you make yourself are private to your organization.
        </p>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Search the shared list before making your own, so the same material is not entered twice</li>
          <li>Use MOQ, SSQ, ROP, and POQ to support purchasing and replenishment workflows</li>
          <li>Finished goods sold to customers are managed under Org Admin → Products, not here</li>
          <li>The item cannot be changed after creation — delete and re-add if you linked the wrong item</li>
        </ul>
      </div>
    )
  },

  // Org Admin - Organization Services
  'organization-services': {
    title: 'Organization Services',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Organization Services allows you to extend and define organization-specific service attributes 
          such as pricing, pricing models, and other service-related information. This feature is similar 
          to Organization Materials, but for services defined in the global Services Master managed by System Admin.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Service Selection:</strong> Link to services from the global Services Master</li>
          <li><strong>Pricing Model:</strong> Set organization-specific pricing model (Hourly, Daily, Fixed, Project Based)</li>
          <li><strong>Hourly Rate:</strong> Organization-specific hourly rate for services</li>
          <li><strong>Daily Rate:</strong> Organization-specific daily rate for services</li>
          <li><strong>Fixed Price:</strong> Organization-specific fixed price for services</li>
          <li><strong>Estimated Duration:</strong> Organization-specific estimated duration in hours</li>
          <li><strong>Unit of Measurement:</strong> Organization-specific UOM (Hour, Day, Project, etc.)</li>
          <li><strong>Notes:</strong> Organization-specific notes or additional information</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Add Service"</strong> to create a new organization service</li>
          <li>Select a service from the global Services Master (managed by System Admin)</li>
          <li>Set organization-specific pricing attributes (rates, pricing model, etc.)</li>
          <li>Add estimated duration and unit of measurement if applicable</li>
          <li>Add any organization-specific notes</li>
          <li>Save the service - it will be available for use in your organization's operations</li>
        </ol>

        <h5 className="mt-4">Pricing Models</h5>
        <ul>
          <li><strong>Hourly:</strong> Service charged per hour - set the hourly rate</li>
          <li><strong>Daily:</strong> Service charged per day - set the daily rate</li>
          <li><strong>Fixed:</strong> Service has a fixed price - set the fixed price</li>
          <li><strong>Project Based:</strong> Service priced per project - set the fixed price</li>
        </ul>

        <h5 className="mt-4">Relationship to Services Master</h5>
        <p>
          Organization Services extends the global Services Master (managed by System Admin) with 
          organization-specific attributes. The base service information (name, description, service type) 
          comes from the Services Master, while pricing and other organization-specific details are 
          maintained here.
        </p>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Ensure services are first created in System Admin → Services Master</li>
          <li>Set pricing that reflects your organization's business model</li>
          <li>Use notes to document any special conditions or requirements</li>
          <li>Update pricing regularly to reflect current market rates</li>
        </ul>
      </div>
    )
  },

  // Sales - Product Catalog
  'customers': {
    title: 'Customers',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Customers are the people and businesses you sell to. A customer must exist before you can
          raise a Sale Order or Sale Bill for them, and their details (state, GSTIN, credit terms)
          drive how those documents are taxed and paid.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Individual or Business:</strong> Business customers carry a company name that appears on invoices.</li>
          <li><strong>GSTIN lookup:</strong> For Indian customers, enter the 15-character GSTIN and the legal name and address are filled in from GST records. If the lookup is unavailable you can type the details yourself.</li>
          <li><strong>GST registration:</strong> Regular, Composition or Not registered. Left unchosen, it becomes Regular when there is a GSTIN and Not registered when there is none. A customer with no GSTIN is fine; their bills are reported as sales to an unregistered buyer. The server checks the GSTIN (checksum included) and takes the state from it.</li>
          <li><strong>GST treatment:</strong> The customer&apos;s state decides whether a Sale Bill charges CGST + SGST (same state as your organization) or IGST (different state).</li>
          <li><strong>Credit terms:</strong> Optional credit days, credit limit and payment terms. A credit limit is checked when a bill is issued: going over it needs a manager&apos;s go-ahead. Credit days set the due date that payment reminders use.</li>
          <li><strong>Payment reminders:</strong> Add an email address so the customer can get payment reminders; switch reminders off for a customer from the Receivables tab in Accounts.</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>&quot;+ Add Customer&quot;</strong> and fill in the name, contact and address</li>
          <li>For Indian business customers, enter the GSTIN first to auto-fill the rest</li>
          <li>Use <strong>Edit</strong> to update details, or the print icon to view and print the customer record</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Keep the state correct - it directly affects the tax split on Sale Bills.</li>
        </ul>
      </div>
    )
  },

  'online-store-reports': {
    title: 'Online Store Reports',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          How your online store is doing, for the <strong>Sales Manager and Org Admin</strong>. Choose a period (this month, last month, the financial year, or your own dates).
          The cards show the orders placed and their value, the <strong>net sales</strong> (what you invoiced, before GST, less returns), how many invoices and returns that is, and how many different buyers ordered.
        </p>
        <h5 className="mt-4">What the numbers mean</h5>
        <ul>
          <li><strong>Orders placed</strong> are counted by the day the buyer placed them, whether or not you have invoiced them yet. The note under the cards says how many were cancelled.</li>
          <li><strong>Sales</strong> are the tax invoices made for online orders, before GST, by the invoice date. They are the same figures as Sales → Reports, limited to the store.</li>
          <li><strong>Returned</strong> is the credit notes of returns, by the date of the note, so a return can fall in a later period than its sale.</li>
          <li>The chart shows each day (each month for a long period): the grey bar is what was ordered, the blue bar the net sales.</li>
          <li>Below it: <strong>By product</strong> (quantity sold, and returned in red), <strong>By payment method</strong> and <strong>By state</strong> (where it was delivered). <strong>Download CSV</strong> saves the table you are looking at.</li>
        </ul>
        <h5 className="mt-4">Good to know</h5>
        <ul>
          <li>Delivery charges collected are shown under the cards. They are part of the sales, but not of any product.</li>
          <li>Cancelled orders never reach an invoice, so they are not sales.</li>
        </ul>
      </div>
    )
  },

  'online-store-orders': {
    title: 'Online Store Orders',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Every order a buyer places on your online store arrives here, and in <strong>Sales → Orders</strong> marked <em>Online</em> (it is the same sale order).
          The stock is held for it as soon as it is placed. Use the buttons at the top to see what needs doing: <strong>New</strong> orders to confirm,
          orders <strong>Waiting for payment</strong>, orders <strong>To send</strong> and orders already <strong>Sent</strong>. Search by order number, name, phone or email.
          A red count on <strong>Online Store</strong> in the side menu, and a line on the Dashboard and Sales pages, tell you how many new orders are waiting to be confirmed.
        </p>
        <h5 className="mt-4">Working an order</h5>
        <p>Open an order to see the buyer, the delivery address, the items and the GST. Then:</p>
        <ul>
          <li><strong>Confirm</strong> a new order. The buyer is emailed.</li>
          <li><strong>Mark paid</strong> when the money arrives (UPI or bank transfer), or when cash on delivery is collected. You can note the UTR or receipt number.</li>
          <li><strong>Packing slip</strong> prints a one-page slip for the parcel: who it is for and where, each item with its quantity (no prices), the buyer&apos;s note, the courier, and for cash on delivery the amount to collect. It is on every order that is not cancelled.</li>
          <li><strong>Mark as sent</strong> with the courier or transport name and, if you have them, a tracking number and link. The buyer is emailed the details and sees them on their order page. You can correct them later.</li>
          <li>If your store cancels unpaid orders after a set time (Org Admin → Online Store), an unpaid order shows <em>Lapses</em> with the time, and is cancelled by itself then, with the buyer told. Marking it paid in time stops that.</li>
          <li>A buyer can cancel their own order from their order page until you confirm it, as long as nothing has been paid. You get an email, the order shows as cancelled with <em>Cancelled by the buyer</em> and their reason, and the stock is free again. Once you confirm it, or it is paid, only you can cancel it.</li>
          <li><strong>Cancel order</strong> gives the stock back and tells the buyer. An order with a tax invoice can't be cancelled: use <strong>Return items</strong> (below). If the buyer had paid, refund them yourself and then use <strong>Mark refunded</strong>.</li>
        </ul>
        <h5 className="mt-4">Refunding an order paid online</h5>
        <p>
          For an order shown as <em>Paid online</em>, a <strong>Sales Manager or Org Admin</strong> can give the money back without leaving iBigDo:
        </p>
        <ul>
          <li><strong>Cancel order</strong> has a tick, <em>Also refund … to the buyer now, through Razorpay</em>, ticked by default. One press cancels the order, frees the stock and refunds the whole amount to the buyer&apos;s card or account. The buyer gets one email saying both.</li>
          <li>For an order already cancelled but not refunded, open it and press <strong>Refund … through Razorpay</strong>.</li>
          <li>Razorpay sends the money back in a few working days. If Razorpay refuses (for example the keys changed), nothing changes and the message tells you why.</li>
          <li>Now and then Razorpay accepts a refund and the buyer&apos;s bank or card refuses it later. If you have ticked <em>refund.failed</em> on the webhook, the order is put back as paid, shows a red <strong>Refund failed</strong> note, and you get an email. Press <strong>Refund</strong> again, or pay the buyer back another way and use <strong>Mark refunded</strong>. For a failed refund on a <em>return</em>, the return then shows an amount to pay back by hand.</li>
          <li>If you refund the payment in Razorpay&apos;s own dashboard instead, a cancelled order is marked refunded by itself within moments (through the webhook), so there is nothing more to press. An order still open is not touched: cancel it first, so it is not sent for free.</li>
          <li>Orders paid another way (UPI, bank transfer, cash) are refunded by you; use <strong>Mark refunded</strong> afterwards. Sales operators can cancel, and a manager then refunds.</li>
        </ul>
        <h5 className="mt-4">Tax invoice, stock and the money</h5>
        <p>
          Once an order is confirmed, press <strong>Make tax invoice</strong> in its dialog. One step makes and issues the invoice: the <strong>stock lots are picked for you</strong> (the lot that expires first, then the oldest), the GST is split the way
          the buyer was shown it (by where it is delivered), and the delivery charge is its own line. Issuing takes the stock out of the warehouse. Until then the stock stays held for the order, so it can't be sold twice.
          If there isn&apos;t enough stock, it tells you which item and how much is on hand; receive or adjust the stock and press it again.
        </p>
        <ul>
          <li><strong>The money already received is recorded for you.</strong> An order paid online gets a receipt against the invoice, put in an account called <em>Razorpay - payments to be settled</em> until the payout reaches your bank. A UPI or transfer payment goes into your invoice bank account
          (Org settings), and cash on delivery into your cash-in-hand if you have one. So Receivables does not show the buyer as still owing. If iBigDo can't tell which account, it says so and you record the receipt in Accounts → Receivables as before. Marking an invoiced order paid records its receipt then.</li>
          <li>Then <strong>Mark as sent</strong> with the courier, and <strong>Mark delivered</strong> when it arrives. The buyer sees <em>Being prepared</em> until you record the courier, then <em>Sent</em>, then <em>Delivered</em>: making the invoice does not by itself tell the buyer it is delivered.</li>
          <li>If you refund an order, its receipt is taken back, so your books do not keep money that went back to the buyer. Once an order is invoiced it is a sale: it can't be cancelled, but items can be taken back (see <em>Returns</em> below). So make the invoice when you are ready to send the goods, not before.</li>
        </ul>
        <h5 className="mt-4">The buyer&apos;s tax invoice</h5>
        <p>
          When you press <strong>Make tax invoice</strong> the buyer is emailed, and their order page (the link in their confirmation email) shows <em>View tax invoice</em>. It opens the same GST invoice you print, with
          <em> Print or save as PDF</em>. Only the person with the order link can open it. A credit note made for a return is not shown there; you give the buyer that yourself.
        </p>
        <h5 className="mt-4">Returns</h5>
        <p>For an invoiced order, a <strong>Sales Manager or Org Admin</strong> presses <strong>Return items</strong>, enters how many of each item are coming back (and ticks the delivery charge if you are giving it back), and records the return:</p>
        <ul>
          <li>A <strong>credit note</strong> is made against the tax invoice, with GST reversed as on the invoice. It shows in Sales → Credit / Debit Notes.</li>
          <li>The goods go <strong>back into stock</strong> at their original cost, unless you untick that (for damaged goods).</li>
          <li>If the buyer had paid online through Razorpay, the credited amount is <strong>refunded there</strong> at once. If Razorpay refuses, nothing is recorded. For any other payment the screen tells you the amount to <strong>pay the buyer back yourself</strong>; the books already show it as paid out.</li>
          <li>The buyer is emailed. You can return in parts; when the whole invoice is credited the order shows as <em>Returned</em>.</li>
          <li>The receipt that was recorded for the order is replaced by one for what the buyer still holds, so Receivables and your bank or Razorpay account stay right.</li>
          <li>An order on credit terms whose invoice would take the customer over their limit is refused; a manager can issue it from Sales with an override.</li>
        </ul>
      </div>
    )
  },

  'online-store': {
    title: 'Online Store',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The Online Store is a page of your own where buyers see your products, their photos, prices and whether they are in stock. It is off until you
          switch on <strong>We sell online</strong>, and when it is off nothing about it shows in the app. Your plan must include it.
        </p>
        <h5 className="mt-4">Setting it up</h5>
        <ul>
          <li><strong>Store name and address.</strong> The address is the last part of your store's web address, for example <code>/shop/your-store</code>. Small letters, numbers and hyphens.</li>
          <li><strong>Sell from warehouse.</strong> Buyers see &quot;in stock&quot; from this warehouse only (never how many you hold).</li>
          <li><strong>Prices include GST.</strong> Tick this if the prices you set already contain the GST; otherwise GST is shown on top.</li>
          <li><strong>How buyers can pay.</strong> Choose at least one of UPI, bank transfer and cash on delivery.</li>
          <li><strong>Unpaid orders.</strong> An order paid by UPI or bank transfer holds its stock from the moment it is placed, even if the buyer never pays. Set a number of hours (24 is one day) and such an order is cancelled automatically when it is still unpaid by then: the stock is freed and the buyer is told. Buyers see the time on their order page and in their email. Cash on delivery orders and paid orders are never cancelled this way. Leave it empty to keep unpaid orders until you cancel them yourself.</li>
          <li><strong>Shipping.</strong> A shipping charge, an order value above which shipping is free, and the smallest order you accept.</li>
          <li><strong>Shipping by weight and place.</strong> Under <em>How shipping is charged</em>, choose <em>By weight and place</em> instead of one flat charge. Make a zone for the states you charge alike (for example your own state), with a charge for the first kilos and a charge for each further kilo started, and an <em>Everywhere else</em> zone for all other states. Then set each product&apos;s weight in kg in <strong>Online Store &rarr; Products online</strong> (a Weight column appears). At checkout the buyer is charged for the cart&apos;s total weight by the zone of their state; before they choose a state they see the <em>Everywhere else</em> charge as an estimate. The free-shipping amount still waives it. A product with no weight counts as weighing nothing, and a warning tells you how many such products are on your store. If a state is in no zone and you have no <em>Everywhere else</em> zone, the buyer is told you cannot deliver there yet. To go back, choose <em>One flat charge</em>.</li>
          <li><strong>Policies.</strong> Shipping, returns and terms of sale appear on the store's own pages.</li>
          <li><strong>Let buyers find my store by name.</strong> On by default. When someone types a few letters of your store&apos;s name on <strong>ibigdo.com/shop</strong>, your store is suggested (with your logo, town and tagline), as long as it is switched on and has a product to show. Your store address works either way; switch this off if you only share your link yourself.</li>
          <li><strong>Grievance officer.</strong> Name the person a buyer can complain to, with an email or a phone. They appear in the store&apos;s footer and on its Shipping and returns page. India&apos;s e-commerce rules expect one to be named: check the wording with your accountant.</li>
          <li><strong>Ready to announce?</strong> At the top of this page a box lists what the store still needs, worked out from what you have already set up (it disappears when nothing is left). Items marked <em>needed</em> stop the store taking orders: a warehouse, a way to pay, a published product with a price, your address and a contact. The <em>recommended</em> ones are what buyers expect to see: photos, shipping and returns policies, your GSTIN, a grievance officer, and weights when shipping is by weight.</li>
        </ul>
        <h5 className="mt-4">Online payments with Razorpay</h5>
        <p>
          With your own Razorpay account, buyers can pay at once by card, UPI, net banking or wallet. The money goes straight to <strong>your Razorpay account</strong>; iBigDo never holds it.
          Set it up in <strong>Org Admin → Online Store → Online payments with Razorpay</strong> (save the store settings first):
        </p>
        <ol>
          <li>In Razorpay, copy your <strong>Key id</strong> and <strong>Key secret</strong> (Account &amp; Settings → API Keys) and paste them. Start with a <em>test</em> key (rzp_test_…): nothing real is charged, and the screen says <em>Test mode</em>.</li>
          <li>Press <strong>Test the keys</strong> to check Razorpay accepts them.</li>
          <li>In Razorpay add the <strong>webhook</strong> the screen shows (its address, a secret you choose, and the events listed). Type the same secret in the Webhook secret box. The webhook means a payment is never lost if a buyer closes the page right after paying.</li>
          <li>Switch on <strong>Let buyers pay now</strong> and save. Checkout now offers <em>Pay now</em>.</li>
          <li>When you are ready for real payments, put in your <em>live</em> keys (rzp_live_…) and a webhook for them.</li>
        </ol>
        <p>
          The order is created <strong>only after the payment is confirmed</strong>, so an unpaid online order never holds your stock, and it shows in Orders as <em>Paid online</em>, ready to confirm and send.
          If the order can&apos;t be completed (for example the last item sold to someone else while the buyer was paying), the payment is <strong>refunded at once</strong> and the buyer is told. If even that refund fails, you get an email to refund it
          from your Razorpay dashboard. The key secret and webhook secret are saved encrypted and are never shown again; a blank box keeps what is saved. To cancel a paid online order and give the money back, see <em>Refunding an order paid online</em> under Online Store Orders: it is one button.
        </p>
        <h5 className="mt-4">Trade prices for business customers</h5>
        <p>
          A business customer can have their own way into your store. In <strong>Sales → Customers</strong>, press <strong>Store link</strong> on the customer (it shows only while
          the store is on) and send the link on WhatsApp or by email. When they open it, the store shows <strong>their price list's prices</strong> (see Price Lists; products the list has
          no price for keep the usual price), and what they order is theirs: their business name and GSTIN, not whoever types an email. If the customer has <strong>credit days</strong> on their
          record, they can choose <em>On my credit terms</em> at checkout: nothing to pay now, and an order that would take them over their credit limit is refused. Orders from a trade link
          show a <em>Trade</em> mark in Orders and are confirmed by you as usual.
        </p>
        <p>
          The link works for a year and each customer can have several. Treat it like a key: whoever has it can order on the customer's account. <strong>Stop</strong> in the same dialog switches a
          link off at once, and you can see how often it was opened. They can leave trade prices from the banner at the top of the store.
        </p>
        <h5 className="mt-4">Choosing what is published</h5>
        <p>
          Once the store is on, the <strong>Online Store</strong> module appears in the side menu. Its <strong>Products online</strong> tab lists your
          products; tick the ones to put on the store. Only active products with a price can be published. Photos and the video link are added in
          Org Admin → Products (Edit).
        </p>
        <h5 className="mt-4">What a buyer does</h5>
        <p>
          A buyer adds products to a cart, checks out with their name, phone, email and delivery address (a business can add its GSTIN for a tax invoice),
          chooses how to pay, and agrees to your terms. GST is worked out from the delivery state: CGST and SGST in your own state, IGST elsewhere, and none
          when your organization does not charge GST. Prices, stock and shipping are always checked by the server, so an order never goes through for
          something you no longer have.
        </p>
        <p>
          The order is an ordinary <strong>sale order</strong> (marked <em>Online</em>) in <strong>Sales → Orders</strong>, and it reserves the stock at once.
          The buyer's details become a customer (a customer with the same email keeps their record as it is). The buyer and you both get an email;
          the buyer's link shows their order, its progress and how to pay: a UPI QR for the UPI ID in Org settings, the invoice bank account for a bank
          transfer, or nothing for cash on delivery. Mark the order paid yourself when the money arrives. Cancel an order to give its stock back.
        </p>
      </div>
    )
  },

  'price-lists': {
    title: 'Price Lists',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          A price list gives a kind of customer their own prices: for example <em>Wholesale</em>, <em>Dealers</em> or a key account. This tab appears only when an
          Org Admin has switched on <strong>We charge different customers different prices</strong> in Org Admin → Org settings, and only for the Org Admin and
          Sales Manager.
        </p>
        <h5 className="mt-4">Making a list</h5>
        <ul>
          <li><strong>+ New price list</strong>, give it a name. You can also give a <strong>percent off the standard price</strong>, which applies to every product that has no fixed price in the list.</li>
          <li><strong>Prices</strong> lists your products with their standard price. Type a price (before GST) for the ones that get their own price; leave the rest empty. The price a product would get from the percentage is shown in grey.</li>
          <li><strong>Edit</strong> changes the name, the percentage, or whether the list is in use. A list that is not in use gives no prices but keeps them and its customers.</li>
          <li>A list that customers are on can't be deleted; move them to another list first, or switch it off.</li>
        </ul>
        <h5 className="mt-4">Giving a customer a list</h5>
        <p>
          On the customer's form in <strong>Sales → Customers</strong>, choose a <strong>Price list</strong>. From then on a quotation, sale order or sale bill for that customer
          starts each product from the list's price, with <em>Price list Wholesale 2,025</em> under the price. You can still change a line, and <strong>Use</strong> puts the list price back.
          The list's price comes before the rate the customer was last billed or quoted. A product the list has no price for keeps working as before.
          The same prices appear in your online store when the customer opens it with their <strong>Store link</strong> (Sales → Customers).
        </p>
        <h5 className="mt-4">Good to know</h5>
        <ul>
          <li>Prices are before GST; GST is added on the document as usual.</li>
          <li>Changing a list changes the starting price on documents made after that; documents already made keep their prices.</li>
          <li>Who changed which price, and when, is in the Edit Log.</li>
        </ul>
      </div>
    )
  },
  'product-catalog': {
    title: 'Product Catalog',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Product Catalog lists organization sellable products with list prices and basic attributes.
          Use it as a reference when creating orders or quoting customers.
        </p>

        <h5 className="mt-4">Sending the catalog to a customer</h5>
        <p>
          <strong>Share catalog</strong> sends a link, on WhatsApp or by email, to a page with your products, their photos and prices that opens
          without logging in. Choose all products or one category, whether prices show (with the GST on top) and whether each product is shown as in
          stock or out of stock (never how many you hold). The page is always as current as the day it is opened: a changed price or photo shows at once.
          It works for 90 days, can be stopped from the same window, and shows how often it was opened. A customer can search it, tap a photo to
          enlarge it, and ask about a product on WhatsApp in one tap (when your organization has a phone number). Only active products are shown.
        </p>
        <p>
          Photos are added to a product in <strong>Org Admin → Products</strong> (Edit). A product with no photo shows a plain picture frame.
        </p>

        <h5 className="mt-4">Maintaining products</h5>
        <p>
          To add or edit products (SKU, pricing, linkage to material master), use{' '}
          <strong>Org Admin → Products</strong>. Prices shown here use your organization&apos;s default currency.
        </p>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Inactive products remain visible but are marked inactive—avoid using them on new orders when possible.</li>
          <li>Stock quantity reflects current product-level stock if maintained by your workflows.</li>
        </ul>
      </div>
    )
  },

  // Sales Module - Reports
  'sales-reports': {
    title: 'Sales Reports',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Sales and gross profit for any period: month by month, by customer and by product. Choose a period at the top (the
          financial year runs April to March) or enter your own dates.
        </p>

        <h5 className="mt-4">What the figures mean</h5>
        <ul>
          <li><strong>Sales</strong> are before GST: the taxable value of issued sale bills dated in the period, less credit notes and plus debit notes dated in it. Opening balance bills are not sales.</li>
          <li><strong>Gross profit</strong> is sales less the cost of the stock sold: the cost of each lot the goods came from, as received, with services (freight, unloading) added; for goods you made, their production cost.</li>
          <li>Goods returned on a credit note come off both sales and cost. A discount or price change on a note changes sales only.</li>
          <li>If a lot has no cost recorded, its sales are left out of gross profit and margin, and the report says how much that is.</li>
          <li><strong>Share</strong> is each customer&apos;s or product&apos;s part of total sales.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Sort out who your best customers are and which products earn the most, not just which sell the most.</li>
          <li>A red margin means that customer or product sold below cost in the period.</li>
          <li><strong>Download CSV</strong> gives the table for Excel.</li>
          <li>Only the Org Admin and the sales and finance managers see this tab, as it shows margins.</li>
        </ul>
      </div>
    )
  },

  'purchase-reports': {
    title: 'Purchase Reports',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          What you bought in any period: month by month, by vendor and by item, with how each item&apos;s price has moved, and the purchase
          orders still waiting for goods. Choose a period at the top (the financial year runs April to March) or enter your own dates.
        </p>

        <h5 className="mt-4">What the figures mean</h5>
        <ul>
          <li><strong>Spend</strong> is before GST: the taxable value of vendor bills and non-GST slips dated in the period (by the vendor&apos;s own date), less the purchase returns issued in it. Cancelled bills are left out.</li>
          <li><strong>By vendor</strong> shows each vendor&apos;s spend and share, what you sent back to them, and when you last had a bill from them.</li>
          <li><strong>By item</strong> shows the quantity bought, the average rate, the <strong>last rate</strong> and the <strong>change</strong> against the buy before it, whoever the vendor was. A rise is red, a fall green. Freight, services and expenses are together under &quot;Services and expenses&quot;.</li>
          <li><strong>Open orders</strong> lists approved purchase orders that are not fully received, how much is still to come, and which are past the date you wanted them. It is as of today, whatever period you chose.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Check the change column before you order: an item whose rate crept up may be worth a call to another vendor.</li>
          <li>A vendor with a large share and no one else to buy from is a risk worth knowing about.</li>
          <li><strong>Download CSV</strong> gives the table in view for Excel.</li>
          <li>Only the Org Admin and the procurement and finance managers see this tab, as it shows what things cost.</li>
        </ul>
      </div>
    )
  },

  // Sales Module - Quotations
  'quotations': {
    title: 'Quotations',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          A quotation tells a customer what you&apos;ll charge: your products, quantities and rates, any discount, and GST, valid
          until a date. Send it on WhatsApp or by email, or print it, follow it up, and when the customer says yes, turn it into a sale bill
          or sale order without typing anything again.
        </p>

        <h5 className="mt-4">Write a quotation</h5>
        <ol>
          <li>Click <strong>+ New Quotation</strong> and choose the customer (add new customers under Customers first).</li>
          <li>Add the products. The rate and GST fill in from the product and its item&apos;s GST rate; change them as needed. For a customer you have billed before, the rate (and discount) is what you last billed them, shown under the rate with the bill and date; <strong>Use</strong> puts it back if you change it.</li>
          <li>It is valid for 15 days unless you change <strong>Valid until</strong>. Add your terms: delivery time, payment, freight.</li>
          <li>GST is CGST + SGST for a customer in your state and IGST for one in another state, as on a sale bill.</li>
        </ol>

        <h5 className="mt-4">Send and follow up</h5>
        <ul>
          <li><strong>Send</strong> opens WhatsApp with a message and a link to the quotation, or switch to <strong>Email</strong> to send the same message and link from iBigDo to the customer&apos;s email. The customer opens it without logging in and can save it as a PDF. You see when they opened it.</li>
          <li><strong>Print / PDF</strong> prints it with your logo and address.</li>
          <li>Mark it <strong>accepted</strong> or <strong>declined</strong> (with the reason, so you learn why you lose deals). An open quotation past its date shows as <strong>Expired</strong>.</li>
          <li>Prices changed? <strong>Edit</strong> it while it is open, or <strong>Copy as new</strong> for a revised quotation.</li>
        </ul>

        <h5 className="mt-4">Customer said yes</h5>
        <p>
          <strong>Make sale bill</strong> opens a new sale bill filled in from the quotation: choose where the goods go from and the
          lots, then save. <strong>Make sale order</strong> does the same for a sale order. The quotation is then marked accepted
          and shows which bill or order it became.
        </p>
      </div>
    )
  },

  'orders': {
    title: 'Sale Order',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          A Sale Order records a customer's commitment to buy—an advance booking or an order awaiting
          fulfillment. Creating one reserves stock (shown as "Reserved" in Inventory → Stock Levels) but
          does <strong>not</strong> touch physical stock or the ledger.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Zero inventory/ledger impact:</strong> Only Sale Bill (the tax invoice) reduces physical stock and posts accounting entries.</li>
          <li><strong>Last rate:</strong> a product starts at the rate this customer was last billed for it (or quoted, if never billed), shown under Unit Price.</li>
          <li><strong>Fulfillment location:</strong> Choose delivery from a warehouse, or pick-up from a retail store—this is where stock gets reserved.</li>
          <li><strong>Status tracking:</strong> PENDING, CONFIRMED, PROCESSING, SHIPPED, DELIVERED, CANCELLED—automatically advances to PROCESSING/DELIVERED as Sale Bills are issued against it.</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Add Order"</strong>, match or create a customer, and add line items (product, quantity, price)</li>
          <li>When ready to actually ship/hand over goods, go to the <strong>Sale Bill</strong> tab and create a bill against this order</li>
        </ol>
      </div>
    )
  },

  'salebills': {
    title: 'Sale Bill',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          A Sale Bill is the tax invoice issued when goods are physically handed over or dispatched to a
          customer. Issuing a bill automatically creates a Goods Issue Note (reducing physical stock) and
          posts Accounts Receivable / Sales Revenue / Tax ledger entries—unlike a Sale Order, which has no
          inventory or ledger effect.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Last rate:</strong> for a customer you have billed before, each product starts at the rate and discount you last billed them, shown under Unit Price with the bill and date (or the rate you last quoted, if never billed). <strong>Use</strong> puts it back if you change it.</li>
          <li><strong>Optional Sale Order link:</strong> Bill against an existing Sale Order (capped at what was ordered), or create a standalone sale with no order.</li>
          <li><strong>Customer: details or walk-in:</strong> Either search/create a customer (a phone number or email is enough for a new one), or choose <em>Walk-in (no details)</em> for a counter sale where no buyer information is recorded at all - it is one or the other.</li>
          <li><strong>GST split:</strong> CGST + SGST for intra-state customers, IGST for inter-state, based on the customer's and organization's state.</li>
          <li><strong>Draft vs Issued:</strong> Save as DRAFT to keep editing; Issue to lock it in, create the GIN, and post the ledger entries (a lot must be selected for each line first).</li>
          <li><strong>Credit limit:</strong> If issuing the bill would take the customer over their credit limit, you get a warning with the figures. A manager can issue it anyway by giving a reason, which is kept on the bill (shown as an <em>Over limit</em> badge). Everyone else needs to collect a payment first or ask a manager.</li>
          <li><strong>Corrections:</strong> An issued bill can&apos;t be edited. Use <em>Credit note</em> or <em>Debit note</em> on the row; they are listed in the Credit / Debit Notes tab.</li>
          <li><strong>e-Invoice and e-Way bill:</strong> When switched on in Org Admin &rarr; Settings, the <em>e-Invoice / e-Way</em> button registers the bill (IRN and QR code, printed on the bill) and raises an e-way bill for goods in transit. Until a live GST provider is connected, these are sandbox numbers marked as such, not government registrations.</li>
          <li><strong>Cancelling:</strong> an IRN or e-way bill can be cancelled only within 24 hours (the screen shows until when). After that, correct a sale with a credit note; an unused e-way bill simply lapses.</li>
          <li><strong>Print / PDF:</strong> <em>View &amp; Print</em> shows the tax invoice as GST rules ask: your and the customer&apos;s GSTIN and state, place of supply with its code, each item&apos;s HSN, quantity and unit, rate and GST %, the tax, the total in words, reverse charge, and a signature block. Choose the copy (original for recipient, duplicate for transporter, triplicate for supplier) and print, or save as PDF from the print dialog. Set the bank account, your UPI ID (for a pay-by-UPI QR code) and the terms printed on it in Org Admin &rarr; Org settings. A composition dealer&apos;s bill prints as a bill of supply.</li>
          <li><strong>Send on WhatsApp or by email:</strong> the green button on an issued bill opens WhatsApp with a message (edit it if you like) and a link to the invoice; switch to <strong>Email</strong> to send it to the customer&apos;s email instead (filled in from their record; replies go to your organization&apos;s email). The customer opens it without logging in: the bill with its items, HSN and GST, what is still due (after receipts and credit or debit notes), and a &ldquo;Pay by UPI&rdquo; button when your UPI ID is set in Org settings. The link works for 30 days and can be stopped; the dialog shows how often it was opened.</li>
          <li><strong>Vehicle and delays:</strong> on an active e-way bill, <em>Change vehicle</em> after a breakdown or transhipment (or <em>Add vehicle</em> if none was given), and <em>Extend validity</em> if the goods are delayed, from 8 hours before the validity ends to 8 hours after. Every change is listed under the e-way bill.</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>"+ Create Sale Bill"</strong>, optionally select a Sale Order, and confirm the customer</li>
          <li>Choose a Sale Bill movement type and the issuing location (warehouse, retail store, or production unit)</li>
          <li>Add line items with product, quantity, price, tax rate, and the inventory lot to issue from</li>
          <li>Click <strong>"Issue Bill"</strong> to finalize—this reduces stock and posts the accounting entries</li>
        </ol>
      </div>
    )
  },

  'sale-notes': {
    title: 'Credit / Debit Notes',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          A tax invoice can&apos;t be edited once it is issued. When something changes afterwards, you issue a
          <strong> credit note</strong> (the customer owes less: a return, a discount) or a
          <strong> debit note</strong> (the customer owes more: a correction, an extra charge) against that bill.
          This tab lists every note and lets you print them.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Raised from the bill:</strong> Use the <em>Credit note</em> or <em>Debit note</em> button on an issued bill in the Sale Bill tab. A note always refers back to its bill.</li>
          <li><strong>Sales returns can restock:</strong> Tick &quot;Take the returned goods back into stock&quot; and the goods return to the location the bill issued from, ready to sell again.</li>
          <li><strong>GST follows the bill:</strong> A note keeps the bill&apos;s place of supply and tax split, and carries its own tax figures so it can go into GSTR-1.</li>
          <li><strong>Books update:</strong> The receivable, sales and tax entries are reversed (credit note) or added (debit note); the customer&apos;s balance on the bill changes with it.</li>
          <li><strong>Held credit:</strong> If a credit note is for more than the bill still owes, the extra stays as credit for that customer. Use <em>Apply credit</em> to set it against their other bills.</li>
          <li><strong>e-Invoice:</strong> If e-invoicing is switched on for your organization, an <em>e-Invoice</em> button registers the note and prints the IRN and QR code on it.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>A note can&apos;t be deleted or cancelled. If one was a mistake, issue the opposite note.</li>
          <li>Reasons are the ones GST asks for: sales return, post-sale discount, deficiency in services, correction in invoice, others.</li>
        </ul>
      </div>
    )
  },

  'purchase-invoices': {
    title: 'Purchase Invoices',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Records the vendor&apos;s tax invoice so you can claim <strong>input tax credit</strong> (GST you paid on purchases).
          Goods you have already received on a GRN stay on the vendor&apos;s payable at the GRN value; the invoice
          adds the GST and any difference in value. Returns to a vendor are recorded here too, as purchase return notes.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Tied to GRNs:</strong> Pick the received goods the invoice covers. A GRN can be invoiced once per vendor.</li>
          <li><strong>Goods with no GRN:</strong> If you pick items but tick no GRN, say where the goods are. <em>Received with this bill</em> adds them to stock now: choose the warehouse, and a GRN is made from the bill&apos;s lines (each needs an item and a quantity). <em>Still to arrive</em> records the bill now; when the goods come, the GRN is raised against this bill, which adds the stock without owing the vendor a second time. Either way the vendor is owed the goods once. A bill recorded before this choice existed, with no GRN, can be marked from its details with <em>Goods still to arrive</em>.</li>
          <li><strong>Vendor&apos;s invoice details:</strong> Their invoice number and date, plus a booking date if you record it later. The due date is worked out from the vendor&apos;s credit days unless you enter one; Accounts &rarr; Payables shows what is due when.</li>
          <li><strong>Claim or don&apos;t claim, per line:</strong> Untick <em>Claim</em> for GST you can&apos;t take credit for; it is then added to the cost, with a short reason.</li>
          <li><strong>As printed:</strong> Tick &quot;Enter GST amounts as printed on the invoice&quot; when the vendor&apos;s rounding differs from the calculated tax.</li>
          <li><strong>Reverse charge (RCM):</strong> For purchases where the vendor didn&apos;t charge GST and you owe it directly.</li>
          <li><strong>Purchase returns:</strong> <em>Return note</em> on an invoice sends goods back and issues a debit note to the vendor, reducing what you owe and the credit you claim. If the note sent goods back and e-way bills are on, <em>e-Way</em> beside it raises the e-way bill for them.</li>
          <li><strong>Cancel:</strong> A mistaken invoice can be cancelled with a reason; its ledger entries are reversed. A GRN its goods came in on (made with the bill, or raised against it) is reversed too, which is refused if any of that stock has been used.</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>&quot;Record vendor invoice&quot;</strong> and choose the vendor</li>
          <li>Tick the received GRNs the invoice covers. Each one fills in its lines (item, quantity and value) for you to check against the invoice</li>
          <li>For other lines, start typing in <em>Description</em> to pick one of your items (its HSN and unit fill in), or type anything else, such as freight</li>
          <li>Enter or check the HSN, quantity, unit price and GST % for each line, and untick <em>Claim</em> where credit isn&apos;t allowed. Quantity times unit price fills in the <em>Taxable value</em>; or type the taxable value as printed on the invoice and the unit price is worked out from it. Whichever you typed last stays as you typed it</li>
          <li>Save. The credit you can claim is shown on the invoice and feeds GSTR-3B and the 2B reconciliation</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>A vendor with no GSTIN gives you no claimable credit (unless it is reverse charge). Add their GSTIN on the vendor first.</li>
        </ul>
      </div>
    )
  },

  'non-gst-purchases': {
    title: 'Non-GST Purchases',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Records goods, services and expenses you bought <strong>with no GST</strong>: a slip (&quot;kachchi parchi&quot;) from a mandi
          trader, farmer or small shop, or a bill from a vendor who is not registered. There is no tax and no input credit; the
          vendor is simply owed the amount, so it shows in their balance and you can pay it from Accounts &rarr; Vendor Payments.
          For a normal bill with GST, use Purchase Invoices instead.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Goods, services and expenses:</strong> Each line is goods (booked to Purchases), or a service or expense (booked to an expense ledger you choose, or to <em>Non-GST Services &amp; Expenses</em>).</li>
          <li><strong>The slip&apos;s number is optional:</strong> Many slips have none. Every purchase gets our own number (PS-year-month-number) either way. If you do enter a slip number, the same number can&apos;t be recorded twice for one vendor.</li>
          <li><strong>Goods already received:</strong> Tick the GRNs the slip covers. They are already on the vendor&apos;s account from the receipt, so only any difference in value is booked. Ticking a GRN fills in its lines for you.</li>
          <li><strong>Goods with no GRN:</strong> Say where they are. <em>Received with this bill</em> adds them to stock now in the warehouse you choose (pick each item from the list and enter its quantity). <em>Still to arrive</em> records the slip now; raise the GRN against it when the goods come, so the vendor isn&apos;t owed for them twice. An older slip with goods and no GRN can be marked from its details with <em>Goods still to arrive</em>.</li>
          <li><strong>Pick your items:</strong> On a goods line, start typing in <em>Description</em> to pick one of your items, or type anything that isn&apos;t in the list.</li>
          <li><strong>For your accountant:</strong> Choose a period and download the list as a CSV. These purchases appear in GSTR-3B as non-GST inward supplies, never as input credit, and are left out of the GSTR-2B reconciliation.</li>
          <li><strong>Photo of the slip:</strong> Attach up to 3 photos (the front and back, say) or a PDF when you record the purchase, or later from its details. Photos are shrunk on your phone or computer before they upload. Anyone who can record a purchase can add them; only a manager can remove one. They can&apos;t be changed once the purchase is cancelled.</li>
          <li><strong>Cancel:</strong> A mistaken entry can be cancelled with a reason; its ledger entries are reversed. A return note can&apos;t be raised against one, so cancel and record it again, or send the goods back with a goods issue note.</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>&quot;Record non-GST purchase&quot;</strong> and choose the vendor</li>
          <li>Enter the slip number and date if the slip has them</li>
          <li>Add a line for each item: pick goods, service or expense, describe it and enter the amount</li>
          <li>Save. The amount is added to what you owe the vendor</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>If the vendor has a GSTIN you are warned, because a registered vendor normally gives a GST invoice. Use this only when they really did not charge GST, for example exempt or nil-rated goods.</li>
          <li>Take the photo straight away, while you still have the slip. A clear photo is the proof behind the purchase.</li>
          <li>Large cash payments to one person in a day can lose their tax deduction. Ask your accountant about the current limit.</li>
        </ul>
      </div>
    )
  },

  'receivables': {
    title: 'Receivables',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Who owes you what, and how late it is. Each customer&apos;s unpaid bills are split by how many days past the due
          date they are, with advances and held credit netted off. From here you can also email payment reminders.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Aging:</strong> Not due, 1-30, 31-60, 61-90 and over 90 days, by customer, as of any date you pick. Customers over their credit limit are flagged.</li>
          <li><strong>Statement:</strong> Open a customer&apos;s statement for every bill, receipt and note in order, with a running balance.</li>
          <li><strong>Payment reminders:</strong> Email a customer from here (<em>Remind</em>; you see the message first and can send it or not), send everything that is due at once with <em>Send due reminders now</em>, or let iBigDo do it every morning.</li>
          <li><strong>One email per customer:</strong> A customer with several unpaid bills gets one email. When they have both overdue and not-yet-due bills, the email lists them in separate sections.</li>
          <li><strong>Your rules:</strong> Open <em>Reminder settings</em> to switch automatic reminders on, choose how many days before the due date to start, how often to repeat once overdue, the most reminders per bill, who replies go to, and the wording.</li>
          <li><strong>How it looks:</strong> Reminders are formatted emails headed by your organization&apos;s logo (set in Org Admin), or its name if there is no logo, with the bills as a list. A plain-text copy goes along for mail apps that can&apos;t show formatting.</li>
          <li><strong>Opt-out:</strong> Switch off reminders for a single customer in their row.</li>
          <li><strong>Log:</strong> Every reminder is recorded, with whether it was sent or failed and why.</li>
          <li><strong>WhatsApp or email:</strong> the green button in a customer&apos;s row opens WhatsApp with a message (edit it if you like) and a link to their statement of dues; switch to <strong>Email</strong> to email it instead. The link opens without logging in, shows the bills still unpaid on the day it is opened, works for 30 days and can be stopped. You press send in WhatsApp, from your own number. The dialog shows the links sent before and how often the customer opened each.</li>
          <li><strong>Pay by UPI:</strong> add your UPI ID in Org Admin &gt; Org settings and the statement shows a &ldquo;Pay with a UPI app&rdquo; button and a QR code for the amount due. Record the payment as a receipt when it reaches your bank.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>A customer needs an email address on their record to get a reminder, and a phone number (with country code) for WhatsApp to open their chat directly.</li>
          <li>Reminders go out under your organization&apos;s name from the platform&apos;s mail address; replies go to the address you set.</li>
          <li>Automatic reminders need email to be set up on the server; the settings card tells you if it isn&apos;t.</li>
        </ul>
      </div>
    )
  },

  tds: {
    title: 'TDS',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Only for a business that deducts TDS (the Org Admin turns it on in the organization profile; most small businesses
          don&apos;t need it). When you pay a contractor, a professional, rent or commission, part of the payment is kept back as TDS and
          paid to the government instead. This tab shows what is owed and by when, records each deposit, and gives you each
          quarter&apos;s details for the TDS return.
        </p>

        <h5 className="mt-4">How it works</h5>
        <ol>
          <li><strong>On the vendor</strong> (Purchase &gt; Vendors), choose what they are paid for under <strong>TDS on payments</strong>. Their PAN is read
            from the GSTIN; without a GSTIN, type it. Without a PAN the higher rate (20%) applies.</li>
          <li><strong>When you pay them</strong> (Accounts &gt; Vendor Payments), the TDS is worked out from the rate and the limits: a single payment above
            the single limit, or the year&apos;s payments to them passing the annual limit (then the earlier payments of the year are caught up
            too). The bank pays the amount less TDS; the vendor is settled for the full amount.</li>
          <li><strong>Deposit it</strong> by the 7th of the next month (30 April for March): tick the payments, enter the date, the account it was paid
            from, and the challan number and BSR code, then <strong>Record deposit</strong>.</li>
          <li><strong>Each quarter</strong>, download the details (CSV) and give them to your CA or TDS software for the return (Form 140,
            formerly 26Q), due 31 July, 31 October, 31 January and 31 May.</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>TDS kept back sits under Duties &amp; Taxes in the Balance Sheet (one TDS Payable account per section) until it is deposited.</li>
          <li>A deposit entered by mistake can be undone by the Finance Manager or Org Admin; its TDS shows as owed again.</li>
          <li>Rates and limits are set for 2026-27 and can be changed under <strong>Rates and limits</strong> if the law changes. Check with your CA.</li>
          <li>Set your TAN in the organization profile; it goes on every challan and return.</li>
        </ul>
      </div>
    )
  },
  'opening-balances': {
    title: 'Opening Balances',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          If your business ran before it started on iBigDo, enter here what it had on the day you started, so your Balance Sheet,
          receivables and stock begin from the right figures. Each balance is posted on the opening date; the owner&apos;s capital
          is worked out as the balancing figure (Opening Balance Equity).
        </p>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Set the date your balances are as at, usually the day before your first transaction in iBigDo (for example 31 March).</li>
          <li><strong>Bank, cash, loans and other accounts:</strong> pick the account and enter its balance. Money you have is a debit; a loan you owe is a credit.</li>
          <li><strong>What you owe vendors:</strong> one total per vendor (or an advance you paid them). Pay it later with a vendor payment not tied to a purchase order.</li>
          <li><strong>Customers&apos; unpaid bills:</strong> one line per bill, with its number, date, due date and the amount still unpaid. Each appears in Receivables, statements and reminders, and receipts can be applied to it like any bill. These are not sales: they never appear in GST returns or sales reports.</li>
          <li><strong>Stock in hand:</strong> each item at each location, with its quantity and cost per unit. It becomes a lot you can issue and sell straight away, and is valued at that cost in your statements.</li>
        </ol>

        <h5 className="mt-4">Import from Tally</h5>
        <p>
          Coming from Tally? In Tally, export Masters (All Masters) as XML and upload the file under <strong>Import from Tally</strong>.
          The preview shows every line and what it becomes in iBigDo; nothing is saved until you choose <strong>Bring in</strong>, and
          then all ready lines are saved together or none are.
        </p>
        <ul>
          <li><strong>Bank and cash</strong> ledgers become bank and cash accounts (created if missing; add a bank&apos;s account number later if Tally didn&apos;t have it).</li>
          <li><strong>Capital, loans, fixed assets, deposits, duties &amp; taxes</strong> and other balance-sheet ledgers are posted to the account of the same name, or one is created in the right group.</li>
          <li><strong>Sundry Debtors:</strong> each unpaid bill comes in with its number, date and credit period. <strong>Sundry Creditors:</strong> one balance per vendor.</li>
          <li><strong>Stock:</strong> each item&apos;s quantity and value by godown, into the warehouse, store or unit of the same name, or the place you choose.</li>
          <li>Customers, vendors and items are matched by name and must exist first: import parties from Org Admin &gt; Data Import with the same file.</li>
          <li>Income and expense ledgers are left out (they start the year at zero), and so is anything already entered, so you can fix what was left out and upload the same file again.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Take the figures from your last balance sheet or trial balance; your CA can help.</li>
          <li>A mistake? Remove the line and enter it again. A bill with money already received against it, or stock already issued, can&apos;t be removed.</li>
          <li>Bank and cash accounts that had an opening balance typed on them are listed at the top, ready to fill in.</li>
          <li>Customers and vendors must exist first: add them under Sales and Purchase, or import them from Tally under Org Admin &gt; Import / Export (Accounts &gt; Import / Export for the finance roles).</li>
          <li>The Tally export carries these balances, and each customer&apos;s unpaid bills by their own numbers. Stock isn&apos;t exported, so in Tally enter the stock items&apos; opening quantities and values too, or Tally shows the stock value as a difference in opening balances.</li>
        </ul>
      </div>
    )
  },

  'financial-statements': {
    title: 'Financial Statements',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The Profit &amp; Loss, Balance Sheet, Trial Balance and Day Book, any ledger&apos;s statement, and the sales and
          purchase registers, prepared from your books for any period: what your CA, your bank and your income tax return need. Nothing has to be entered here; every GRN, bill, note, payment and
          receipt you record is already in them.
        </p>

        <h5 className="mt-4">What is here</h5>
        <ul>
          <li><strong>Profit &amp; Loss:</strong> sales, less the cost of goods sold (opening stock, plus goods bought, less closing stock), gives the gross profit; then other income and expenses give the net profit.</li>
          <li><strong>Balance Sheet:</strong> what the business owns and owes on a date, grouped as Sundry Debtors, Sundry Creditors, Duties &amp; Taxes, Stock-in-Hand, Bank and Cash. Click a group to see its ledgers.</li>
          <li><strong>Trial Balance:</strong> every ledger with its opening balance, debits, credits and closing balance for the period. Debits and credits always tally.</li>
          <li><strong>Day Book:</strong> every entry in the period, in date order, with the ledgers it debited and credited.</li>
          <li><strong>Ledger:</strong> one ledger&apos;s entries with the balance after each, from the opening balance to the closing. Choose a customer (Sundry Debtors) or a vendor (Sundry Creditors) for the party ledger your CA asks for or the party wants to match against their books; it carries their GSTIN and address. Click a ledger in the Trial Balance, or <strong>Ledger statement</strong> on a customer&apos;s statement in Receivables or a vendor&apos;s bills in Payables, to open it.</li>
          <li><strong>Registers:</strong> the sales and purchase registers, line by line with HSN, GST rate and tax, as CSV. The purchase register goes by the vendor&apos;s bill date, so it matches the vendor&apos;s records and GSTR-2B (the date each bill was entered is a column too), and lists non-GST slips and the debit notes you raised on vendors (as negatives).</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Pick a statement, then a period: this or last financial year (April to March), this or last month, or your own dates. The Balance Sheet is as at the last date.</li>
          <li><strong>Download CSV</strong> for your CA or a spreadsheet, or <strong>Print</strong> (with your logo and name) to save as PDF.</li>
        </ol>

        <h5 className="mt-4">How stock is valued</h5>
        <p>
          Closing stock is worked out lot by lot from your goods receipts and issues: what is left of each lot on the date, at the
          cost the books carry for it (its value plus any handling or transport allocated to it). Keep receipts and issues up to
          date, and issue against the right lot, for an accurate figure.
        </p>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>The Profit &amp; Loss A/c on the Balance Sheet is the profit brought forward from earlier years plus this year&apos;s.</li>
          <li>If the Balance Sheet shows a difference in opening balances, an opening balance entered on a ledger has no matching entry. Check them in Accounts &rarr; Ledgers.</li>
          <li>GST you collect is owed to the government and GST you can claim is an asset, so neither appears in the Profit &amp; Loss; GST you can&apos;t claim is a cost.</li>
        </ul>
      </div>
    )
  },

  'gst-returns': {
    title: 'GST Returns',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Working papers for your monthly GST returns, prepared from your sale bills, credit/debit notes and vendor
          invoices. <strong>Nothing is filed from here.</strong> Download the files, have your accountant check them, and
          file on the GST portal.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>GSTR-1 (sales):</strong> Sales to registered buyers (B2B), large and small sales to consumers, credit/debit notes, nil-rated sales, HSN summary and the documents issued. Download as portal JSON or as CSVs.</li>
          <li><strong>GSTR-3B (summary):</strong> Outward tax, reverse-charge inward supplies and the input credit you can claim, with a section-by-section view.</li>
          <li><strong>GSTR-2B reconciliation:</strong> Upload the GSTR-2B JSON you download from the portal. It is matched against your vendor invoices and purchase returns so you can see credit you can&apos;t claim yet, invoices you haven&apos;t recorded, and differences in amounts.</li>
          <li><strong>Warnings:</strong> Anything that would trip the portal, such as a missing GSTIN or HSN, is listed above the tables.</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Pick the return month</li>
          <li>Review GSTR-1 and GSTR-3B and read the warnings</li>
          <li>Download the portal JSON (needs your organization&apos;s GSTIN in Org Admin &rarr; Settings &rarr; GST Tax Profile) or the CSVs</li>
          <li>Download GSTR-2B from the portal and upload it to reconcile your purchases</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Fill in HSN codes on your items and the state on your customers and vendors first; most warnings come from missing details there.</li>
          <li>To move books to Tally, use Org Admin &rarr; Import / Export.</li>
        </ul>
      </div>
    )
  },

  'job-work': {
    title: 'Job Work',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Job work is when you send your material to someone else to process and they send it back. It is a
          <strong> delivery challan</strong>, not a sale: the goods stay yours, and the law gives a deadline for them to come
          back. This tab tracks what you sent, what is still out, and what returned.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Challan:</strong> Pick the job worker (a vendor), the inventory lots to send and the quantities. Sending takes the stock out of the location it came from and prints a delivery challan.</li>
          <li><strong>Nature of job work:</strong> Inputs have one year to come back; capital goods three. Each challan shows its deadline as <em>Within time</em>, <em>Due soon</em> or <em>Past deadline</em>.</li>
          <li><strong>e-Way bill:</strong> When e-way bills are switched on, <em>e-Way</em> on a challan raises one for the goods sent (a job work delivery challan). It is needed whatever the value when the job worker is in another state.</li>
          <li><strong>Record goods back:</strong> Enter what came back and what was wasted or lost. Returned goods go into stock at the cost they carried out, so the job worker&apos;s charge is not mixed in here.</li>
          <li><strong>Still with job workers:</strong> A live list of material that is yours but off-site, by job worker, with days left.</li>
          <li><strong>ITC-04:</strong> Choose a financial year and quarter for a working of goods sent and received back, to help with the quarterly ITC-04 return.</li>
        </ul>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Click <strong>&quot;New job work challan&quot;</strong>, choose the job worker and where you are sending from</li>
          <li>Add the lots and quantities, set the expected return date, and save to issue the challan</li>
          <li>When goods return, use <strong>Record goods back</strong> on the challan</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Add the job worker as a vendor first, with their GSTIN and state.</li>
          <li>The ITC-04 file is a working to give your accountant, not a filed return.</li>
        </ul>
      </div>
    )
  },

  'partner-assistant': {
    title: 'Search & Ask',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Two steps. First <strong>search the help</strong>: type a few words, like <em>tally export</em>, and open the topic, which also
          says where the screen is. It is instant and has no limit. If the help does not have what you need, <strong>ask the assistant</strong>
          below the search: it answers from iBigDo&apos;s own help, any time, without waiting for a reply.
        </p>
        <p>After a search, <strong>Ask the assistant instead</strong> carries your words down to the question box.</p>

        <h5 className="mt-4">Getting the most from the assistant</h5>
        <ul>
          <li>Ask one thing at a time, in plain words. English, Hindi or Hinglish all work.</li>
          <li>You can follow up (&quot;and for the Finance Manager?&quot;). Click <strong>Start a new question</strong> to begin on a fresh topic.</li>
          <li>It answers only from the help. When the help does not cover a question it says so and marks the answer <strong>Not fully covered by the help</strong>.</li>
          <li>It does not see any customer&apos;s data, and it does not know current prices or plan limits: see Plans &amp; Pricing for those.</li>
        </ul>

        <h5 className="mt-4">When it cannot help</h5>
        <ul>
          <li>Click <strong>Send this to support</strong> under the answer. The question and the answer go to the iBigDo team, and the reply appears under Help &amp; Support, My Requests.</li>
          <li>Use the thumbs up or down to tell us whether an answer helped. Questions it could not answer are how we find what the help is missing.</li>
        </ul>

        <h5 className="mt-4">Limits</h5>
        <ul>
          <li>There is a daily limit on questions per person; the page shows how many are left today.</li>
        </ul>
      </div>
    )
  },

  'partner-guides': {
    title: 'Sales Guides',
    content: (
      <div>
        <h5>Overview</h5>
        <p>Three guides, in the order you use them with a customer:</p>
        <ol>
          <li><strong>Discovery questions:</strong> what to ask on the first calls, why each question helps, and how the answers point to a plan and a demo. Start with the eight marked Must ask.</li>
          <li><strong>Demo playbook:</strong> how to prepare the demo and what to show, stage by stage.</li>
          <li><strong>Onboarding guide:</strong> what to collect from the customer, the setup steps in iBigDo, the trial run, go-live and the first 30 days.</li>
        </ol>
        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Prices and plan limits change: always take them from Plans &amp; Pricing, not from memory.</li>
          <li>The guides list what iBigDo doesn&apos;t do yet. Read that part before every first call, so you never promise it.</li>
          <li>Below the guides is the <strong>brochure</strong>: a two-page PDF to download and share, with a short WhatsApp message to go with it (<em>Copy the WhatsApp message</em>). It has no prices.</li>
        </ul>
      </div>
    )
  },
  'sysadmin-dashboard': {
    title: 'Dashboard',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The platform at a glance, for System Admin and Tech Support: how many organizations and people use iBigDo, and who is on it right now. System Admin also sees the customer funnel; Tech Support sees the support tickets instead.
        </p>

        <h5 className="mt-4">What you see</h5>
        <ul>
          <li><strong>Organizations, Total Users, Total Roles, System Admins:</strong> the counts across the whole platform.</li>
          <li><strong>Active Now:</strong> people who have used iBigDo in the last 15 minutes.</li>
          <li><strong>Customer funnel (System Admin only):</strong> how many organizations signed up, signed in, used iBigDo in the last 30 days, have ever paid and pay now, with the share of sign-ups each stage is. Beside it, how many months to 1,000 paying customers at the pace of the last three whole months (new paying less stopped). Below, a month-by-month table of sign-ups and who paid, and a table of who brought them: each account manager, each affiliate, and organizations that came with no partner.</li>
          <li><strong>Support tickets (Tech Support):</strong> how many tickets are <em>open</em> (new, in progress or reopened), how many are <em>stale</em> (open with nothing done for 3 or more days), how many are <em>resolved and waiting on the sender</em> (they close by themselves after 14 days), and how many were closed this week, with the open ones by type and a <em>Waiting longest</em> list. Click a ticket in that list to open its thread in the inbox.</li>
          <li><strong>Organizations Overview:</strong> one row per organization with its ID, users, active users and roles. <strong>View Details</strong> opens that organization under Organizations.</li>
        </ul>

        <h5 className="mt-4">How the funnel counts</h5>
        <ul>
          <li><strong>Paying</strong> is an organization on a plan that costs money, with the plan not yet ended. An organization you set up by hand on a paid plan counts too. <strong>Has ever paid</strong> adds those whose plan has since ended (they are the &quot;stopped&quot; ones).</li>
          <li>iBigDo&apos;s own internal organizations are left out. Recent months look low on &quot;have paid&quot; because trials are still running.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Use it as a quick health check before a maintenance window: Active Now tells you how many people a restart would interrupt (see Announcements to warn them first).</li>
        </ul>
      </div>
    )
  },

  'sysadmin-organizations': {
    title: 'Organizations',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Every customer organization on the platform. Create one, then open it to manage its people, roles, plan, account manager and to see how it uses iBigDo.
        </p>

        <h5 className="mt-4">The list</h5>
        <ul>
          <li>Each row shows the organization&apos;s <strong>plan and status</strong> (trial, paying, expired, or no plan), <strong>when the plan ends</strong> (in orange within 14 days, in red once past), how many <strong>users</strong> it has, <strong>when it was last used</strong> by anyone, and when it <strong>signed up</strong>. A line above the list counts how many are on trial, paying and expired.</li>
          <li>Search by name, email, country or ID, and filter by plan: on trial, paying, expired, no plan, or <strong>ending within 14 days</strong> (the ones to call before they lapse). Click a heading to sort; the newest sign-ups come first.</li>
          <li><strong>Create Organization</strong> opens a panel: name and email are required; description, phone, website, address, city, district, state, country, postal code, default currency and a logo address are optional.</li>
          <li><strong>View Details</strong> on a row opens the organization.</li>
        </ul>

        <h5 className="mt-4">Inside an organization</h5>
        <ul>
          <li><strong>Users:</strong> create users (<strong>+ Create User</strong>), edit them, give roles, assign the Org Admin role, or promote someone to System Admin, with each person&apos;s last login and last active time. The buttons are icons; hover one for its name.</li>
          <li><strong>Roles:</strong> the roles and permissions that organization has.</li>
          <li><strong>Subscription:</strong> <strong>Assign / Change Plan</strong> with a plan, status, start date and end date, and the <strong>Plan History</strong> of every change and who made it. Choosing a paid plan sets the status to <strong>Active</strong> (and the trial plan sets <strong>Trial</strong>) for you; if you leave a paid plan on Trial, the form warns you. A customer shows as paying only when the status is Active.</li>
          <li><strong>Account Manager:</strong> the account managers serving this organization. <strong>Add Account Manager</strong> sets the commission terms: an initial rate for a number of months, a trailing rate after that, an optional cap (an amount, a number of months, or both) and a start date. Ending an assignment stops commission for that organization and ends that account manager&apos;s access to it.</li>
          <li><strong>Usage Metrics:</strong> what has been set up (warehouses, production units, retail stores, products, materials, services) and recent activity (total GRNs and GINs, transactions today, last password sign-in and last time any member used iBigDo, last GRN date; "any member" is the same set of people the organizations list counts for Last active, including someone whose own organization is another one). Useful for spotting a customer who signed up but never started.</li>
          <li><strong>Role history:</strong> every role given or taken, when, to whom, how and by whom.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>An organization with no active plan is limited to the Org Admin screen until a plan is assigned, so check Subscription when a customer says they are locked out.</li>
          <li>Give and take roles through this screen or Org Admin &gt; Users, never by editing the database, so Role history stays complete.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-system-admins': {
    title: 'System Admins',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The people who can administer the whole platform, not just one organization.
        </p>

        <h5 className="mt-4">What you see</h5>
        <ul>
          <li>Each System Admin with their email, name and the organization their login belongs to.</li>
          <li><strong>Demote</strong> takes away their System Admin access.</li>
        </ul>

        <h5 className="mt-4">Adding one</h5>
        <ol>
          <li>Open Organizations and the organization the person belongs to.</li>
          <li>On its Users tab, promote the user to System Admin.</li>
        </ol>

        <h5 className="mt-4">Tech Support</h5>
        <p>
          Below the list is <strong>Tech Support</strong>: iBigDo staff who answer customers but cannot change settings. They have their
          own <strong>Tech Support</strong> module in the side menu (System Admin sees it too), with the Dashboard, Organizations (a look
          at the people, plan, usage and role history, plus a set-password link for someone who is locked out), Feedback, Assistant
          Questions, and Material Master and Services Master (to review, tidy and merge, not to create or delete). They do not see the
          System Admin module: email, plans, commissions, account managers, tax rates or any other setting.
        </p>
        <ul>
          <li><strong>Give Tech Support access:</strong> choose the organization the person belongs to, then the person, then <strong>Give access</strong>.</li>
          <li><strong>Take away</strong> ends it at once.</li>
          <li>It is a switch on the person that only a System Admin can set, so no customer&apos;s Org Admin can give it.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Keep this list short. Demote someone as soon as they no longer need platform access.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-uom': {
    title: 'Units of Measurement',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The units iBigDo knows, with how they convert into each other. Organizations pick from these for their materials.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Name and Symbol:</strong> for example Kilogram and kg.</li>
          <li><strong>Type:</strong> Weight, Length, Volume, Count, Area, Time or Temperature. Units convert only within a type.</li>
          <li><strong>Factor to base:</strong> how many base units make one of this unit: mL = 1, L = 1000; g = 1, kg = 1000. Goods receipts and issues are converted into the material&apos;s stocking unit with this factor.</li>
          <li><strong>Active / Inactive</strong> and a filter by type. System units are kept by iBigDo and have some fields locked. <strong>Print</strong> gives a list.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Check the factor twice before saving: stock quantities are converted with it.</li>
          <li>Switch a unit off rather than deleting one that organizations already use.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-services-master': {
    title: 'Services Master',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The shared list of services (work or charges bought or sold by the hour, day or project) and their types. Organizations pick from it, or draft their own.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Services Master:</strong> name, service code, service type, pricing model (Hourly, Daily or Project Based), the rate or price, estimated duration, unit of measurement, description and active status.</li>
          <li><strong>Service Types Master:</strong> the groups services belong to. <strong>+ Create Service Type</strong> adds one.</li>
          <li><strong>Merge:</strong> when an organization drafts a service that already exists, merge the draft into the right one. Every reference to the draft (organization services, GRNs, purchase orders) moves to the service you keep and the draft is deleted. This cannot be undone.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Check for an existing service before creating one, and merge duplicates early, while few documents refer to them.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-goods-movement-types': {
    title: 'Goods Movement Types',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The reasons stock moves, such as Receive from Vendor or Return to Vendor. Every goods receipt and goods issue picks one.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Code and Name:</strong> the code is how other screens refer to the type.</li>
          <li><strong>Type (direction):</strong> Inbound (stock comes in) or Outbound (stock goes out). The list can be filtered by it.</li>
          <li><strong>Reference Type (optional):</strong> the document it goes with: Purchase Order, Procurement Survey, Work Order, Sales Order, Sale Bill or Stock Transfer Order.</li>
          <li><strong>Location Type:</strong> Any, or only Warehouse, Retail Store or Production Unit, to limit where goods of this type can be received or issued.</li>
          <li>Description, active status and <strong>View &amp; Print</strong>.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Add a type rather than changing the meaning of one in use, so old documents keep reading true.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-tax-rates': {
    title: 'Tax Rates',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The GST rate slabs organizations choose from on sale bill lines.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Name and Rate %:</strong> for example GST 18% and 18.</li>
          <li>Description and active status; add, edit or remove a slab from Actions.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>When a GST rate changes, add the new slab and switch the old one off, instead of changing its rate, so earlier bills are not affected.</li>
          <li>An item&apos;s usual rate is set per organization under Org Admin &gt; Materials; this list is only the slabs on offer.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-subscription-plans': {
    title: 'Subscription Plans',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The plans customers can be put on, with their prices, trial length, limits and the features each includes. The plans are shown side by side, so one look shows how they differ: every limit, the support and onboarding terms, and a tick for each feature a plan includes. The pencil under a plan&apos;s name edits it, the bin deletes it. Inactive plans are folded away by default so the comparison shows what customers can buy; switch on <strong>Show inactive plans</strong> to see them (the choice is remembered in your browser), for example to reactivate one with its pencil.
        </p>
        <p>
          Below the plans, the <strong>Reseller price list terms</strong> are shown as account managers see them. Press <strong>Edit</strong> to change them, then <strong>Save terms</strong> (or Cancel to leave them as they were).
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Name, description, price per month and per year, trial days.</strong></li>
          <li><strong>Limits:</strong> warehouses, production units, retail stores, daily transactions, users and documents per month. Leave a limit blank for unlimited.</li>
          <li><strong>Who it is for, onboarding fee, and support first response:</strong> in how many business days iBigDo replies if the account manager has not.</li>
          <li><strong>Features included:</strong> the features that differ between plans. Everything else is in every plan.</li>
          <li><strong>Default:</strong> only one plan can be the default. A new organization starts on it, for its trial days. The default plan is not offered to Org Admins as a plan to request.</li>
          <li><strong>Visible to account managers:</strong> controls whether it shows under Account Management &gt; Plans &amp; Pricing. Leave it off while you prepare a plan, and for the trial and legacy plans.</li>
          <li><strong>Active / inactive.</strong></li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Prices and limits shown to account managers and customers come straight from here, so a change takes effect on the next view.</li>
          <li>Make a new plan rather than changing one customers are on, unless you mean every customer on it to change.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-partners': {
    title: 'Account Managers',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The resellers and sales staff who sell to and support customer organizations for a commission.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Name, contact person, email, phone and notes</strong> for each account manager, and whether they are active.</li>
          <li><strong>Login:</strong> an account manager gets a login when you first assign them to an organization (Organizations &gt; the organization &gt; Account Manager). Until then the list says so.</li>
          <li>Edit or delete from Actions; one with history is better made inactive.</li>
        </ul>

        <h5 className="mt-4">How it fits</h5>
        <ul>
          <li>Their assignments, with the commission terms for each organization, are on the organization. What they have earned is under Commissions.</li>
          <li>They see Account Management in iBigDo: plans and pricing, their own organizations, their customers&apos; support tickets, the sales guides and the assistant.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-media': {
    title: 'Media',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Where product photos are kept. By default they are stored in the database. With an S3 bucket behind a CDN (<code>media.ibigdo.com</code>)
          they are files, the database keeps only their keys, pages are lighter, and a product can have up to 10 photos instead of 5.
        </p>

        <h5 className="mt-4">Turning it on</h5>
        <ol>
          <li>Set <code>MEDIA_STORAGE=s3</code>, <code>MEDIA_S3_BUCKET</code>, <code>MEDIA_S3_REGION</code>, <code>MEDIA_PUBLIC_BASE_URL</code> and the AWS access key
            (<code>AWS_ACCESS_KEY_ID</code>, <code>AWS_SECRET_ACCESS_KEY</code>) in the server&apos;s environment, and restart. This page then says S3.</li>
          <li>New photos go to the bucket from then on. Photos already in the database keep working.</li>
          <li><strong>Move photos from the database to the bucket</strong> copies the old ones across, a few at a time, until none are left. A photo leaves the database only
            after both of its files are stored, so stopping half-way loses nothing: press it again to carry on.</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Moving takes a moment per photo; leave the page open until it says Done.</li>
          <li><code>MEDIA_KEY_PREFIX</code> (for example <code>dev/</code>) keeps a test setup apart from production files in the same bucket.</li>
          <li>The bucket stays private; only the CDN reads it.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-partner-applications': {
    title: 'Account Manager Applications',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          People who applied to become an account manager on the public page <strong>ibigdo.com/partners</strong>. Use that address in campaigns: add
          <code> ?utm_source=...&amp;utm_campaign=...</code> to it and each application shows where it came from.
        </p>

        <h5 className="mt-4">What you see</h5>
        <ul>
          <li>Each application with the person&apos;s name, firm, email, phone, city, how they describe themselves, how many small businesses they work with and their note. A count of those waiting is at the top.</li>
          <li><strong>Approve</strong> makes the account manager record under Account Managers, filled in from the application. It gives no access yet: agree the terms with them, then assign their first customer, which is when their login is made.</li>
          <li><strong>Not now</strong> keeps the application with a note for yourself. The applicant is not told.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>An applicant is emailed a short thank-you, and the feedback notification address is told of each new application.</li>
          <li>The form refuses a second application from an email that is still waiting, and one from an email that already belongs to an active partner.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-affiliates': {
    title: 'Affiliates',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Affiliates are accountants, advisers or anyone else who refers businesses to iBigDo for a share of what those businesses pay.
          Unlike account managers they do not work the customer and never see any customer data: only their own link, the businesses they
          referred (name, whether paying, which plan) and what they have earned.
        </p>

        <h5 className="mt-4">Program</h5>
        <ul>
          <li>The <strong>switch</strong> turns the program on or off: with it off, the Referred by box is gone from sign-up and codes stop working. What was earned stays.</li>
          <li>The <strong>default terms</strong> are what a new referral gets: a rate for the first months, a lower rate after, and how many months from the referral it earns for (empty: for ever).</li>
          <li>A referral <strong>keeps the terms it was made with</strong>. Changing the defaults, or an affiliate&apos;s own terms, only affects referrals made afterwards.</li>
        </ul>

        <h5 className="mt-4">Adding an affiliate</h5>
        <ol>
          <li>Add affiliate: name, email, optionally their own terms. They get a referral code and link. Account managers can also add affiliates themselves (Account Management &gt; Affiliates): no approval, the program&apos;s terms, an invitation at once, at most 10 a day; the <strong>Added by</strong> column shows who.</li>
          <li><strong>Invite</strong> gives them a login (a set-password link goes to their email). Someone who already has an iBigDo login just sees the Affiliate screen next time they sign in.</li>
          <li>They share the link or code. A business that types it at sign-up is credited to them from that day.</li>
        </ol>

        <h5 className="mt-4">Referred organizations</h5>
        <ul>
          <li>Lists every referral, with how it was made and the terms. <strong>Attach</strong> credits an affiliate to an organization that forgot the code at sign-up; an organization has one affiliate at a time. <strong>End</strong> stops it earning from now on.</li>
          <li>An account manager on the same organization is paid separately and is not reduced by the affiliate.</li>
        </ul>

        <h5 className="mt-4">Paying them</h5>
        <p>
          Commission accrues each time the organization&apos;s payment is recorded as paid, and shows in Partner Commissions (marked Affiliate).
          Paying out happens outside iBigDo: mark the line paid once you have. Check with your accountant how tax applies to what you pay out.
        </p>
      </div>
    )
  },

  'sysadmin-partner-commissions': {
    title: 'Partner Commissions',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          What account managers and affiliates have earned. Commission accrues by itself when an organization&apos;s payment is recorded as paid; paying the account manager happens outside iBigDo.
        </p>

        <h5 className="mt-4">How a commission is worked out</h5>
        <ul>
          <li>From the terms on the account manager&apos;s assignment to that organization: the <strong>initial rate</strong> for the first months after the start date, then the <strong>trailing rate</strong>.</li>
          <li>A <strong>cap</strong> stops it: once the capped amount has accrued, or the capped number of months has passed, nothing more accrues, and the line is marked <strong>Capped</strong>.</li>
        </ul>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li>The ledger lists the date, account manager, organization, payment amount, rate, tier (initial or trailing), commission and status. Filter by account manager or by Accrued / Paid.</li>
          <li><strong>Mark Paid</strong> records that you have paid it out.</li>
          <li><strong>Record refund</strong> (on a line that earned commission) is for a refund of that payment that Razorpay did not tell iBigDo about. Enter the amount refunded.</li>
        </ul>

        <h5 className="mt-4">Refunds take commission back</h5>
        <p>
          When part or all of a payment is refunded, the commission it earned is taken back <strong>in proportion</strong>, for the account manager and the affiliate alike. A refund Razorpay processes is picked up by itself
          (it needs the <em>refund.processed</em> event ticked on the webhook in Razorpay); one paid another way is recorded with <strong>Record refund</strong>.
          The ledger shows the reversal as a <strong>negative line marked Refund</strong>, so the totals still add up, and it also frees the commission cap for that amount.
        </p>
        <ul>
          <li>Commission not yet paid out simply nets off to nothing.</li>
          <li>Commission you had <strong>already paid out</strong> is not changed: its reversal shows <strong>Owed back</strong>. Take that amount off the next payout, or ask for it back, then press <strong>Mark settled</strong>.</li>
          <li>There is no time limit: a refund at any time reverses its commission.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Mark a line paid only after the payout has actually been made, since nothing else in iBigDo records it.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-subscription-plan-requests': {
    title: 'Plan Requests',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Plans that Org Admins have asked for from their License screen, waiting for you.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li>Each request shows the organization, the plan, the billing cycle (monthly or yearly), who asked and when, and its status.</li>
          <li><strong>Approve</strong> puts the organization on that plan straight away, as active, from now for one month (monthly) or one year (yearly). It is added to the organization&apos;s Plan History.</li>
          <li><strong>Reject</strong> asks for optional notes, which the Org Admin can read, for example &quot;Please contact billing to arrange payment first&quot;.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Approving does not take payment. Make sure the customer has paid, or that you have agreed terms, first.</li>
          <li>A request that has been approved or rejected cannot be decided again.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-ledger-categories': {
    title: 'Ledger Categories',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The shared categories ledgers are filed under, by type. Organizations choose from them when they create a ledger.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Category name and ledger type:</strong> Asset, Liability, Equity, Revenue or Expense. The list can be filtered by type.</li>
          <li>Description and active status; add, edit or remove from Actions.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Keep categories few and plain: they show in every organization&apos;s chart of accounts and reports.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-email': {
    title: 'Email Settings',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          How iBigDo sends email (invitations, password links, notifications and broadcasts). Nothing is sent until this is set up.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>SMTP host, port, username and password</strong> of the mail account iBigDo sends through.</li>
          <li><strong>From address and From name:</strong> what recipients see.</li>
          <li><strong>Frontend base URL:</strong> the address links in emails open, such as your site&apos;s address. A wrong one makes password and share links point nowhere.</li>
          <li><strong>Feedback notify email:</strong> where a notice goes when someone sends feedback or uses Contact Us.</li>
          <li><strong>Send test email:</strong> sends a message to the recipient you enter, to prove the settings work.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Send a test after every change, to an address you can read.</li>
          <li>If sending stops working, check here first: the usual causes are an expired app password or a changed host.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-feedback': {
    title: 'Feedback',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Everything users send from Help &amp; Support and the Contact Us form, in one inbox, with the conversation on each. System Admin and Tech Support both answer here.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li>The inbox shows the date, category (bug, idea, complaint, other or contact), who sent it, their organization, the message, whether it has a reply, and its status. Filter by status.</li>
          <li><strong>View / respond</strong> opens the whole thread. Add a reply (tick whether to email the person), an <strong>internal note</strong> that only System Admin and the account managers on that ticket can see, and move the status.</li>
          <li>Status goes New, then In Progress, then Resolved, then Closed. Once you have checked that the fix works, choose <strong>Closed</strong> yourself; the person who sent it can also accept the fix (which closes it) or reject it (which reopens it). A closed ticket cannot be changed, and the person is emailed when you close it. A ticket left Resolved for 14 days, with no answer from the person, is closed by itself (and they are emailed); they can always send a new request.</li>
          <li>Account managers see and answer the tickets of their own organizations under Account Management &gt; Support Tickets.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>A reply shows to the person as a red count on the Help icon and a notice when they sign in, so reply from here rather than by email where you can.</li>
          <li>Questions the assistant could not answer arrive here too, marked as from the help assistant.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-announcements': {
    title: 'Announcements',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          A strip across the top of the app and the login page, for telling everyone something such as planned maintenance.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Message</strong> (up to 500 characters) and <strong>Type</strong>: Info or Warning.</li>
          <li><strong>Show from</strong> and <strong>Hide after</strong>: it shows only between these times.</li>
          <li><strong>Maintenance time (optional):</strong> the strip turns amber and counts down in the last 15 minutes. The hide time defaults to one hour after it unless you change it.</li>
          <li><strong>Active</strong> switch, and Edit and Delete. Each row shows whether it is Live, Scheduled, Ended or Inactive.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Post a maintenance notice a day ahead, then again an hour before with the maintenance time set.</li>
          <li>Check Dashboard &gt; Active Now first to see how many people a restart will interrupt.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-email-broadcast': {
    title: 'Broadcast Email',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Send one message to many people, such as an announcement of new features, with each copy carrying the recipient&apos;s own name.
        </p>

        <h5 className="mt-4">How to use</h5>
        <ol>
          <li><strong>Send to:</strong> account managers, Org Admins, all users, or one organization. Duplicate addresses get one copy, and one send can reach at most 500 people.</li>
          <li>Write the <strong>Subject</strong> and the <strong>Message</strong>. Paste from a web page, an email or a document and the formatting comes along, or use the toolbar. HTML is cleaned of anything unsafe.</li>
          <li>Personalize with {'{{firstName|there}}'}, {'{{lastName}}'}, {'{{fullName}}'} and {'{{organization}}'}; the word after | is used when the name is unknown.</li>
          <li><strong>Preview</strong> shows how many will receive it and the message as the first of them sees it. <strong>Send a test</strong> to your own address first.</li>
          <li>Send. Copies go out one at a time, and <strong>History</strong> lists each broadcast with how many were delivered or failed, and the detail for every recipient.</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Email must be set up under Email first; this page says so if it is not.</li>
          <li>A person who is both an account manager and an Org Admin gets one message from each audience you send to. Say so in the message, or send to one audience only.</li>
        </ul>
      </div>
    )
  },

  'sysadmin-shop-directory': {
    title: 'Shop Directory',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The products page of ibigdo.com/shop shows products from stores whose Org Admin chose to be on it (Org Admin &rsaquo; Online Store &rsaquo; Show my products on ibigdo.com/shop). Buyers go on to the seller&apos;s own store to buy, so iBigDo never handles the money. This is where you look after that page.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Stores:</strong> every store that is on the page or has been taken off it, with how many products it shows now and how many open reports it has. <strong>Take off</strong> asks for a reason, which the store&apos;s Org Admin reads in their settings; <strong>Put back</strong> reverses it. The store itself, its address and its orders are never affected.</li>
          <li><strong>Reports:</strong> what buyers send from <em>Report</em> on a product (not as described, fake, should not be sold, wrong price, something else), with their words and email if they gave one. A report takes nothing off by itself.</li>
          <li>Settle a report with <strong>Dismiss</strong>, <strong>Take product off</strong> or <strong>Take store off</strong>. Any other open reports about the same product (or store) are settled with it.</li>
        </ul>

        <h5 className="mt-4">Who is on the page</h5>
        <ul>
          <li>A store appears only when it is switched on, in its plan, not lapsed, has named a grievance officer and its Org Admin has chosen to be there. A product needs to be published, active and priced.</li>
          <li>A change shows on the page at once; otherwise the page is rebuilt every few minutes.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Look at the product before you act: open it from the report. Several reports about one store are a better reason to take the store off than one about a product.</li>
        </ul>
      </div>
    )
  },
  'sysadmin-assistant-questions': {
    title: 'Assistant Questions',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Every question put to the help assistant, by account managers and by customers&apos; users, and above all what the help could not answer. This is the list of what to write into the help next.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>View:</strong> <em>Missed</em> (not covered by the help, or marked not helpful), <em>Flagged</em> (refused as abusive or off topic), or <em>All</em>.</li>
          <li>Each row shows when, who (an account manager, or a customer with their organization number), the question, whether the help covered it, the person&apos;s thumbs up or down, and the tokens used. Open a row to read the answer.</li>
          <li>A failed attempt shows as Failed with the reason.</li>
        </ul>

        <h5 className="mt-4">How to use it</h5>
        <ol>
          <li>Look at Missed regularly. A question that comes up again and again belongs in the help (and in the Getting Started guide if it is a new person&apos;s question).</li>
          <li>After you add it to the help, regenerate the assistant&apos;s text (see DEPLOYMENT.md) so it can answer next time.</li>
          <li>Check Flagged for a person who keeps being refused: three abusive or six off-topic questions in a day pause them for a day.</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Tokens show what each question cost. The limits per person, per organization and per day are set on the server.</li>
        </ul>
      </div>
    )
  },

  'license': {
    title: 'License',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Your organization&apos;s plan, and how to ask for a different one.
        </p>

        <h5 className="mt-4">What you see</h5>
        <ul>
          <li><strong>Plan and Billing Cycle:</strong> the plan you are on, its status and when it ends.</li>
          <li><strong>Compare plans:</strong> every plan side by side: price (yearly and monthly), how many warehouses, production units, retail stores, users and documents a month it allows, how fast your account manager first replies, how soon iBigDo steps in if they have not, onboarding, and which optional features it includes. Your own plan is marked.</li>
          <li><strong>Subscription History:</strong> every change of plan, its status and who made it.</li>
          <li>A notice appears when the plan has expired, is about to end, or a request of yours is waiting.</li>
        </ul>

        <h5 className="mt-4">To request a plan</h5>
        <ol>
          <li>Look through Compare plans, then press Choose this plan under the one you want (or pick it in the form), and choose monthly or yearly billing.</li>
          <li>Submit the request. A System Admin reviews it and activates it for your organization; you see it as pending until then.</li>
        </ol>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>If the plan has expired, only the Org Admin screens open until a new one is activated. Ask for a plan here, or talk to your account manager.</li>
          <li>Ask your account manager or iBigDo support for prices and what each plan includes.</li>
        </ul>
      </div>
    )
  },

  'partner-organizations': {
    title: 'Organizations',
    content: (
      <div>
        <h5>Overview</h5>
        <p>The organizations you are currently the Account Manager for, one row each, with where they stand and what you have earned on them.</p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Plan and Subscription:</strong> the plan the organization is on, its status (for example active or trial) and the date it renews.</li>
          <li><strong>Your terms:</strong> the commission agreed for that organization: the rate for the first months, the rate after that, any cap, and the date you were assigned.</li>
          <li><strong>Paid so far:</strong> what the organization has paid for its subscription to date.</li>
          <li><strong>Commission earned, paid and pending:</strong> what you have earned on those payments, what has already been paid out to you, and what is still pending.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Watch the renewal date: a customer close to renewing is the one to call before the plan lapses.</li>
          <li>Only organizations assigned to you are listed. If one is missing, ask the iBigDo team to check the assignment.</li>
          <li>Your customers&apos; tickets are on the Support Tickets tab.</li>
        </ul>
      </div>
    )
  },

  'partner-tickets': {
    title: 'Support Tickets',
    content: (
      <div>
        <h5>Overview</h5>
        <p>The help requests, ideas and complaints raised by users of the organizations you look after, so you can reply before they wait on the iBigDo team.</p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>The list:</strong> date, category, who raised it, which organization, the start of the message, whether it has a reply, and its status. Use the status filter at the top to see only New or In Progress ones, and Refresh to load new tickets.</li>
          <li><strong>View / Respond:</strong> opens the whole conversation, oldest first. Add a reply, change the status, or add an internal note.</li>
          <li><strong>Email the customer:</strong> ticked by default, so the person who raised the ticket is emailed when you add a reply. Untick it to reply without an email.</li>
          <li><strong>Internal notes:</strong> visible only to iBigDo and account managers on that ticket, never to the customer.</li>
        </ul>

        <h5 className="mt-4">Status</h5>
        <ul>
          <li>A ticket moves New, then In Progress, then Resolved, then Closed. iBigDo closes it after checking the fix, or the person who raised it can accept it (closing it) or reject it (reopening it). One left Resolved for 14 days with no answer closes by itself, and they can send a new request if it is not fixed.</li>
          <li>Saving needs a reply or a note; to only change the status, pick it and add a short reply.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>If you cannot answer a ticket, leave an internal note saying what you tried, so the iBigDo team can pick it up.</li>
          <li>Before replying, try Search &amp; Ask: many questions are already answered in the help.</li>
        </ul>
      </div>
    )
  },

  'partner-compare': {
    title: 'Compare',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          For when a customer asks why iBigDo costs more than Tally, Busy, Zoho Books and Inventory, Marg or Vyapar. It is
          written to be honest: it says where we are behind as well as where we differ.
        </p>

        <h5 className="mt-4">What you see</h5>
        <ul>
          <li><strong>Why are you costlier?:</strong> a short answer in four steps, in your own words.</li>
          <li><strong>What a customer pays:</strong> each product&apos;s cost over 1, 3 or 5 years, before GST, next to our plans. Our prices are always current; the others are as read on the date shown.</li>
          <li><strong>What each one does:</strong> where it runs, users and roles, production, batches, audit trail, e-invoice, payroll and working offline. &ldquo;Not checked&rdquo; means we have not found out, not that the answer is no.</li>
          <li><strong>When not to pitch iBigDo:</strong> the customers a simpler product serves better.</li>
          <li><strong>Where this came from:</strong> the pages the figures were read from.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Their prices and features change and resellers vary. Say &ldquo;about&rdquo; and have the customer check the vendor&apos;s page.</li>
          <li>If you find a difference in your market surveys, raise a Support Ticket so the page is corrected.</li>
        </ul>
      </div>
    )
  },

  'partner-affiliates': {
    title: 'Affiliates',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          An affiliate is an accountant, adviser or anyone else who refers businesses to iBigDo and earns a share of what they pay.
          You can add the ones you know. An affiliate is paid separately and <strong>never reduces your own commission</strong>
          on a customer, whoever referred that customer.
        </p>
        <h5 className="mt-4">Adding one</h5>
        <ul>
          <li>Give the firm or person, their email and, if you like, a contact person and phone. There is no approval step and the terms are iBigDo&apos;s standard ones, which you cannot change.</li>
          <li>They are emailed a login at once; the email says you introduced them. They see only their own link, the businesses they referred (name, whether paying) and what they earn.</li>
          <li>Their link and code are in the table, to send them yourself if you like. A business that types the code, or uses the link, when it signs up is credited to them.</li>
        </ul>
        <h5 className="mt-4">What is refused</h5>
        <ul>
          <li>Your own email or phone, and anyone who is already an account manager or an affiliate (the same email or phone). A person is registered once.</li>
          <li>More than 10 a day.</li>
        </ul>
        <h5 className="mt-4">What you see</h5>
        <p>The affiliates you added, whether they have signed in, and how many businesses each referred. You do not see what they earn.</p>
      </div>
    )
  },

  'partner-plans': {
    title: 'Plans & Pricing',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          The plans you can sell to your customers, exactly as they are set up in ibigdo today. Prices, limits and which
          features each plan includes come straight from the plan catalog, so this page is always current.
        </p>

        <h5 className="mt-4">Key Features</h5>
        <ul>
          <li><strong>Plan cards:</strong> Price per year and per month (some plans are yearly only), locations, users, the monthly document allowance, the onboarding fee and whether it is optional or required, how quickly you are expected to give a first response, and how quickly ibigdo replies to a ticket if you haven&apos;t.</li>
          <li><strong>What each plan includes:</strong> A grid of the features that differ between plans. Everything else (purchasing, stock, sales, GST invoicing, accounts, GSTR-1 and 3B workings, Tally) is in every plan.</li>
          <li><strong>Add-ons, discounts and terms:</strong> Extra users, document top-ups, volume and prepay discounts, reseller margin, the founding offer and support terms.</li>
          <li><strong>Customer quote:</strong> A clean quote for one plan with no margins or reseller terms. Print it or save it as a PDF to give a customer.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Prices are per organization and exclusive of GST.</li>
          <li>The document allowance is monthly and soft: customers are warned at 80% and 100%, and only paused 10% past it.</li>
          <li>The reseller terms are for you. Share the customer quote, not this page.</li>
        </ul>
      </div>
    )
  },

  'edit-log': {
    title: 'Edit Log',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          A record of every change to your books and records: who created, changed or deleted a bill, note, receipt, payment,
          order, GRN, GIN, transfer, adjustment, expense or journal (and each of their lines), or a customer, vendor, product,
          material, ledger, tax rate or bank account, when, and what each field was before and after.
        </p>
        <p>
          It is always on and can&apos;t be switched off, edited or deleted by anyone, which is what the Companies Act asks of
          accounting software (the &ldquo;audit trail&rdquo; or &ldquo;edit log&rdquo;). Your auditor can review it here or in
          the download. The Org Admin finds it in Org Admin and in Accounts; the Finance Manager in Accounts.
        </p>

        <h5 className="mt-4">How to Use</h5>
        <ol>
          <li>Choose the period, and narrow it if you like: one kind of document, one person, or a number or name (such as a bill number or a customer).</li>
          <li><strong>Changed</strong> rows show each field&apos;s old value struck through and the new one. <strong>Created</strong> and <strong>Deleted</strong> rows show what the record held when you click <em>Show</em>.</li>
          <li><strong>Download CSV</strong> gives one row per changed field, for the auditor&apos;s spreadsheet.</li>
          <li>For one document or record, use the clock button (<i className="bi bi-clock-history" />) on its row in its own list: sale bills, quotations, orders, notes, purchase bills and slips, returns, POs, GRNs, GINs, transfers, adjustments, work orders, job work challans, vendor payments, expenses, journals, customers, vendors, products, materials and ledgers. It shows its whole history, its lines included.</li>
        </ol>

        <h5 className="mt-4">Good to know</h5>
        <ul>
          <li>Several steps of one save are one row: a bill created and then given its totals shows once, as created, with its final values.</li>
          <li>Ledger postings show only if one is ever changed or deleted; posting them is what the Day Book already shows.</li>
          <li>&ldquo;The system&rdquo; is a scheduled job, such as payment reminders.</li>
          <li>Changes from before the edit log was added aren&apos;t in it.</li>
        </ul>
      </div>
    )
  },

  'import-export': {
    title: 'Import / Export',
    content: (
      <div>
        <h5>Overview</h5>
        <p>
          Move data between iBigDo and Tally: send your books to Tally, or bring in the customers and vendors you
          already keep there.
        </p>

        <h5 className="mt-4">Export to Tally</h5>
        <ul>
          <li>Downloads a zip with your parties, bank and cash accounts, sales and GST ledgers, stock items and the sales, receipt, payment, purchase, credit note and debit note vouchers for the dates you choose.</li>
          <li>In Tally, import <strong>1-masters.xml</strong> first, then <strong>2-vouchers.xml</strong>. A README in the zip lists what is and isn&apos;t included.</li>
          <li>Leave the dates blank to export everything; opening balances come from your ledgers.</li>
          <li><strong>Match receipts and credit notes to invoices:</strong> tick this to turn on bill-wise tracking for customers in Tally. Each sale becomes a bill, a receipt settles the bills it was applied to here, and a credit note settles the invoice it was issued against. Anything not applied to an invoice in the file stays on the customer&apos;s account, so balances are the same either way. Vendors are not tracked bill by bill. Leave it off if your accountant keeps these ledgers differently, and try it on a test company first.</li>
        </ul>

        <h5 className="mt-4">Import customers and vendors</h5>
        <ul>
          <li>Upload a Tally masters export (in Tally: Export &rarr; Masters) or a CSV. Download the CSV template if you need the column layout.</li>
          <li>You see a preview of what will be created, skipped or flagged. <strong>Nothing is saved until you confirm.</strong></li>
          <li>Existing customers and vendors are never changed; matches (by GSTIN, then name) are skipped.</li>
        </ul>

        <h5 className="mt-4">Import items</h5>
        <ul>
          <li>Upload the same Tally masters export (its stock items) or the items CSV (Name, Unit, HSN, SKU, Category, Sale Price, GST Rate; only Name is needed). From Tally the GST rate comes from the item&apos;s latest GST details.</li>
          <li><strong>Opening stock in the same file:</strong> for stock you already hold, fill <em>Opening Qty</em> and <em>Cost Per Unit</em> (and <em>Warehouse</em>, left blank if you have only one place, and <em>Lot</em>). Each row&apos;s stock is booked as opening stock, the same as under Accounts &rsaquo; Opening Balances, so stock levels and the books agree from the first day. The preview shows each one as &ldquo;500 kg at Main Warehouse @ 42.50 = 21250.00&rdquo;, and asks for the date your stock is as at once, if none is set. A row whose stock can&apos;t be booked (no cost, a unit or place iBigDo doesn&apos;t know) is left out and says why, so no item arrives without its stock. Items already in your Materials are skipped, with their stock.</li>
          <li>An item already in the Global Material Master under the same name is used as it is; anything else is added as your own item. Items already in your Materials are skipped.</li>
          <li>Units are matched by symbol, name or GST unit code (Kgs, Nos, Ltr...). A unit iBigDo doesn&apos;t have is flagged; set it on the item afterwards.</li>
          <li>Choose the category for rows that don&apos;t name one (a Tally stock group with the same name as a category is used instead).</li>
          <li>Tick <strong>Also add them as products you sell</strong> for finished goods; rows with a sale price always become products.</li>
        </ul>

        <h5 className="mt-4">Tips</h5>
        <ul>
          <li>Try the first Tally import on a test company and have your accountant check it.</li>
          <li>The Org Admin sees this tab under Org Admin, and the Finance Manager and Finance Operator under Accounts. They can export to Tally and import customers and vendors; importing items is for the Org Admin only.</li>
        </ul>
      </div>
    )
  }
};

/**
 * Get help content for a feature
 * @param {string} featureId - Feature identifier
 * @returns {object|null} Help content object or null if not found
 */
export const getHelpContent = (featureId) => {
  return helpContent[featureId] || null;
};

